// Recipe catalog for food alternatives
// This file contains all recipes for healthier food alternatives

export interface AlternativeRecipe {
  title: string;
  cuisine: string;
  servings: number;
  caloriesPerServing: number;
  ingredients: Array<{
    name: string;
    amount: number;
    unit: string;
    notes?: string;
  }>;
  steps: string[];
  notes: string;
}

export const RECIPE_CATALOG: Record<string, AlternativeRecipe> = {
  // ============================================
  // BROWNIE RECIPES
  // ============================================
  'black-bean-brownies': {
    title: 'Black Bean Brownies (Gluten-Free)',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 145,
    ingredients: [
      { name: 'black beans', amount: 1.5, unit: 'can', notes: '15 oz, drained and rinsed' },
      { name: 'almond flour', amount: 0.5, unit: 'cup' },
      { name: 'dark chocolate', amount: 4, unit: 'oz', notes: '70% cacao or higher, melted' },
      { name: 'coconut oil', amount: 0.25, unit: 'cup', notes: 'melted' },
      { name: 'maple syrup', amount: 0.33, unit: 'cup' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'cocoa powder', amount: 0.25, unit: 'cup', notes: 'unsweetened' },
      { name: 'baking powder', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.25, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Grease an 8x8 inch baking pan.',
      'In a food processor, blend black beans until smooth.',
      'Melt dark chocolate and coconut oil together.',
      'In a large bowl, mix bean puree, melted chocolate, maple syrup, eggs, and vanilla.',
      'In a separate bowl, mix almond flour, cocoa powder, baking powder, and salt.',
      'Combine wet and dry ingredients. Pour into pan.',
      'Bake for 25-30 minutes until set. Cool before cutting.'
    ],
    notes: 'Black beans provide high fiber and protein. Almond flour keeps these gluten-free. Lower in sugar than traditional brownies.'
  },

  'avocado-chocolate-brownies': {
    title: 'Avocado Chocolate Brownies',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 165,
    ingredients: [
      { name: 'avocado', amount: 1, unit: 'piece', notes: 'large, ripe' },
      { name: 'almond flour', amount: 1, unit: 'cup' },
      { name: 'dark chocolate', amount: 4, unit: 'oz', notes: '70% cacao or higher, melted' },
      { name: 'coconut oil', amount: 0.25, unit: 'cup', notes: 'melted' },
      { name: 'maple syrup', amount: 0.33, unit: 'cup' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'cocoa powder', amount: 0.25, unit: 'cup', notes: 'unsweetened' },
      { name: 'baking powder', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.25, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Grease an 8x8 inch baking pan.',
      'Mash avocado until smooth.',
      'Melt dark chocolate and coconut oil together.',
      'In a large bowl, mix mashed avocado, melted chocolate, maple syrup, eggs, and vanilla.',
      'In a separate bowl, mix almond flour, cocoa powder, baking powder, and salt.',
      'Combine wet and dry ingredients. Pour into pan.',
      'Bake for 25-30 minutes until set. Cool before cutting.'
    ],
    notes: 'Avocado provides healthy monounsaturated fats and creates a rich, fudgy texture without butter. Almond flour keeps these gluten-free and adds protein. Lower in sugar and higher in healthy fats than traditional brownies.'
  },

  'sweet-potato-brownies': {
    title: 'Sweet Potato Brownies',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 150,
    ingredients: [
      { name: 'sweet potato', amount: 1.5, unit: 'cup', notes: 'cooked and mashed (about 2 medium sweet potatoes)' },
      { name: 'almond flour', amount: 1, unit: 'cup', notes: 'blanched almond flour' },
      { name: 'dark chocolate', amount: 4, unit: 'oz', notes: '70% cacao or higher, chopped' },
      { name: 'coconut oil', amount: 0.25, unit: 'cup', notes: 'melted' },
      { name: 'maple syrup', amount: 0.33, unit: 'cup', notes: 'pure maple syrup' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'cocoa powder', amount: 0.25, unit: 'cup', notes: 'unsweetened' },
      { name: 'baking powder', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.25, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Grease an 8x8 inch baking pan or line with parchment paper.',
      'Cook sweet potatoes: Pierce 2 medium sweet potatoes with a fork. Bake at 400°F (200°C) for 45-60 minutes until tender, or microwave for 8-10 minutes. Let cool, then peel and mash until smooth. Measure 1.5 cups.',
      'Melt dark chocolate and coconut oil together in a double boiler or microwave (stir every 30 seconds until smooth).',
      'In a large bowl, whisk together mashed sweet potato, melted chocolate mixture, maple syrup, eggs, and vanilla extract until smooth.',
      'In a separate bowl, mix almond flour, cocoa powder, baking powder, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined. Do not overmix.',
      'Pour batter into prepared pan and spread evenly.',
      'Bake for 25-30 minutes, or until a toothpick inserted into the center comes out with a few moist crumbs (not completely clean).',
      'Let cool completely in the pan before cutting into squares.',
      'Store in an airtight container in the refrigerator for up to 1 week.'
    ],
    notes: 'Sweet potatoes provide natural sweetness, beta-carotene (vitamin A), and fiber. Almond flour adds protein and healthy fats. Dark chocolate provides antioxidants. These brownies are naturally sweet, high in beta-carotene, and have a lower glycemic impact than traditional brownies.'
  },

  'date-sweetened-brownies': {
    title: 'Date-Sweetened Brownies',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 140,
    ingredients: [
      { name: 'dates', amount: 1, unit: 'cup', notes: 'pitted, medjool dates preferred' },
      { name: 'almond flour', amount: 1, unit: 'cup', notes: 'blanched almond flour' },
      { name: 'dark chocolate', amount: 4, unit: 'oz', notes: '70% cacao or higher, chopped' },
      { name: 'coconut oil', amount: 0.25, unit: 'cup', notes: 'melted' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'cocoa powder', amount: 0.25, unit: 'cup', notes: 'unsweetened' },
      { name: 'baking powder', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.25, unit: 'tsp' },
      { name: 'walnuts', amount: 0.5, unit: 'cup', notes: 'chopped, optional' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Grease an 8x8 inch baking pan or line with parchment paper.',
      'Soak dates in hot water for 10 minutes to soften. Drain and reserve 2 tbsp of the soaking water.',
      'In a food processor, blend soaked dates with reserved water until a smooth paste forms.',
      'Melt dark chocolate and coconut oil together in a double boiler or microwave (stir every 30 seconds until smooth).',
      'In a large bowl, whisk together date paste, melted chocolate mixture, eggs, and vanilla extract until smooth.',
      'In a separate bowl, mix almond flour, cocoa powder, baking powder, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined. Fold in walnuts if using.',
      'Pour batter into prepared pan and spread evenly.',
      'Bake for 25-30 minutes, or until a toothpick inserted into the center comes out with a few moist crumbs.',
      'Let cool completely in the pan before cutting into squares.',
      'Store in an airtight container in the refrigerator for up to 1 week.'
    ],
    notes: 'Dates provide natural sweetness, fiber, and minerals (potassium, magnesium). No refined sugar is needed. Almond flour adds protein and healthy fats. Dark chocolate provides antioxidants. These brownies are naturally sweetened, high in fiber, and rich in minerals.'
  },

  'zucchini-chocolate-chip-brownies': {
    title: 'Zucchini Chocolate Chip Brownies',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 160,
    ingredients: [
      { name: 'zucchini', amount: 1.5, unit: 'cup', notes: 'grated (about 1 large zucchini)' },
      { name: 'whole wheat flour', amount: 1, unit: 'cup' },
      { name: 'dark chocolate chips', amount: 0.5, unit: 'cup', notes: '70% cacao or higher' },
      { name: 'coconut oil', amount: 0.25, unit: 'cup', notes: 'melted' },
      { name: 'maple syrup', amount: 0.33, unit: 'cup', notes: 'pure maple syrup' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'cocoa powder', amount: 0.25, unit: 'cup', notes: 'unsweetened' },
      { name: 'baking powder', amount: 0.5, unit: 'tsp' },
      { name: 'baking soda', amount: 0.25, unit: 'tsp' },
      { name: 'salt', amount: 0.25, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Grease an 8x8 inch baking pan or line with parchment paper.',
      'Grate zucchini using a box grater. Place grated zucchini in a clean kitchen towel and squeeze out excess moisture.',
      'In a large bowl, whisk together melted coconut oil, maple syrup, eggs, and vanilla extract until smooth.',
      'In a separate bowl, mix whole wheat flour, cocoa powder, baking powder, baking soda, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Fold in grated zucchini and dark chocolate chips.',
      'Pour batter into prepared pan and spread evenly.',
      'Bake for 30-35 minutes, or until a toothpick inserted into the center comes out with a few moist crumbs.',
      'Let cool completely in the pan before cutting into squares.',
      'Store in an airtight container in the refrigerator for up to 1 week.'
    ],
    notes: 'Zucchini adds moisture, fiber, and vitamins without adding many calories. Whole wheat flour provides more fiber than white flour. Dark chocolate chips provide antioxidants. These brownies are moist, high in fiber, and lower in sugar than traditional brownies.'
  },

  'almond-flour-brownies-keto-friendly': {
    title: 'Almond Flour Brownies (Keto-Friendly)',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 170,
    ingredients: [
      { name: 'almond flour', amount: 1.5, unit: 'cup', notes: 'blanched almond flour' },
      { name: 'coconut oil', amount: 0.5, unit: 'cup', notes: 'melted' },
      { name: 'erythritol or stevia', amount: 0.5, unit: 'cup', notes: 'sugar-free sweetener' },
      { name: 'dark chocolate', amount: 4, unit: 'oz', notes: '85% cacao or higher, sugar-free if possible' },
      { name: 'eggs', amount: 3, unit: 'piece', notes: 'large' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'cocoa powder', amount: 0.25, unit: 'cup', notes: 'unsweetened' },
      { name: 'baking powder', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.25, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Grease an 8x8 inch baking pan or line with parchment paper.',
      'Melt dark chocolate and coconut oil together in a double boiler or microwave (stir every 30 seconds until smooth).',
      'In a large bowl, whisk together melted chocolate mixture, sugar-free sweetener, eggs, and vanilla extract until smooth.',
      'In a separate bowl, mix almond flour, cocoa powder, baking powder, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined. Do not overmix.',
      'Pour batter into prepared pan and spread evenly.',
      'Bake for 20-25 minutes, or until a toothpick inserted into the center comes out with a few moist crumbs.',
      'Let cool completely in the pan before cutting into squares.',
      'Store in an airtight container in the refrigerator for up to 1 week.'
    ],
    notes: 'Almond flour is low in carbs and high in healthy fats. Coconut oil provides medium-chain triglycerides. No refined sugar makes these suitable for low-carb diets. These brownies are low carb, high in healthy fats, and contain no refined sugar.'
  },

  'oat-flour-brownies': {
    title: 'Oat Flour Brownies',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 155,
    ingredients: [
      { name: 'oat flour', amount: 1, unit: 'cup', notes: 'or grind 1.25 cups rolled oats in a food processor' },
      { name: 'dark chocolate', amount: 4, unit: 'oz', notes: '70% cacao or higher, chopped' },
      { name: 'coconut oil', amount: 0.25, unit: 'cup', notes: 'melted' },
      { name: 'maple syrup', amount: 0.25, unit: 'cup', notes: 'pure maple syrup' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'cocoa powder', amount: 0.25, unit: 'cup', notes: 'unsweetened' },
      { name: 'baking powder', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.25, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Grease an 8x8 inch baking pan or line with parchment paper.',
      'If making oat flour: Grind 1.25 cups rolled oats in a food processor until fine flour-like consistency.',
      'Melt dark chocolate and coconut oil together in a double boiler or microwave (stir every 30 seconds until smooth).',
      'In a large bowl, whisk together melted chocolate mixture, maple syrup, eggs, and vanilla extract until smooth.',
      'In a separate bowl, mix oat flour, cocoa powder, baking powder, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined. Do not overmix.',
      'Pour batter into prepared pan and spread evenly.',
      'Bake for 25-30 minutes, or until a toothpick inserted into the center comes out with a few moist crumbs.',
      'Let cool completely in the pan before cutting into squares.',
      'Store in an airtight container in the refrigerator for up to 1 week.'
    ],
    notes: 'Oat flour is a whole grain alternative to white flour, providing more fiber and a lower glycemic index. Dark chocolate provides antioxidants. These brownies are whole grain, higher in fiber, and have a lower glycemic impact than white flour brownies.'
  },

  'coconut-flour-brownies': {
    title: 'Coconut Flour Brownies',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 160,
    ingredients: [
      { name: 'coconut flour', amount: 0.5, unit: 'cup', notes: 'note: coconut flour absorbs more liquid than other flours' },
      { name: 'dark chocolate', amount: 4, unit: 'oz', notes: '70% cacao or higher, chopped' },
      { name: 'coconut oil', amount: 0.5, unit: 'cup', notes: 'melted' },
      { name: 'maple syrup', amount: 0.33, unit: 'cup', notes: 'pure maple syrup' },
      { name: 'eggs', amount: 4, unit: 'piece', notes: 'large (coconut flour needs more eggs)' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'cocoa powder', amount: 0.25, unit: 'cup', notes: 'unsweetened' },
      { name: 'baking powder', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.25, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Grease an 8x8 inch baking pan or line with parchment paper.',
      'Melt dark chocolate and coconut oil together in a double boiler or microwave (stir every 30 seconds until smooth).',
      'In a large bowl, whisk together melted chocolate mixture, maple syrup, eggs, and vanilla extract until smooth.',
      'In a separate bowl, mix coconut flour, cocoa powder, baking powder, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined. Let sit for 5 minutes (coconut flour needs time to absorb liquid).',
      'Pour batter into prepared pan and spread evenly.',
      'Bake for 25-30 minutes, or until a toothpick inserted into the center comes out with a few moist crumbs.',
      'Let cool completely in the pan before cutting into squares.',
      'Store in an airtight container in the refrigerator for up to 1 week.'
    ],
    notes: 'Coconut flour is gluten-free, lower in carbs, and high in fiber. Coconut oil provides healthy medium-chain triglycerides. These brownies are gluten-free, lower in carbs, and contain healthy fats from coconut.'
  },

  // ============================================
  // CAKE RECIPES
  // ============================================
  'banana-bread-made-with-whole-wheat-flour': {
    title: 'Banana Bread Made with Whole Wheat Flour',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 195,
    ingredients: [
      { name: 'ripe bananas', amount: 3, unit: 'piece', notes: 'medium, very ripe (brown spots)' },
      { name: 'whole wheat flour', amount: 2, unit: 'cup' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'coconut oil', amount: 0.33, unit: 'cup', notes: 'melted' },
      { name: 'maple syrup', amount: 0.25, unit: 'cup', notes: 'or honey' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'baking soda', amount: 1, unit: 'tsp' },
      { name: 'cinnamon', amount: 1, unit: 'tsp', notes: 'ground' },
      { name: 'salt', amount: 0.5, unit: 'tsp' },
      { name: 'walnuts', amount: 0.5, unit: 'cup', notes: 'chopped, optional' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Grease a 9x5 inch loaf pan.',
      'Mash ripe bananas in a large bowl until smooth.',
      'Add eggs, melted coconut oil, maple syrup, and vanilla. Mix well.',
      'In a separate bowl, mix whole wheat flour, baking soda, cinnamon, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined. Fold in walnuts if using.',
      'Pour batter into prepared pan.',
      'Bake for 50-60 minutes, or until a toothpick inserted into the center comes out clean.',
      'Let cool in pan for 10 minutes, then transfer to a wire rack.',
      'Store in an airtight container at room temperature for up to 3 days.'
    ],
    notes: 'Naturally sweetened with ripe bananas, made with whole wheat flour instead of white flour. Lower sugar, higher fiber.'
  },

  'carrot-cake-with-almond-flour': {
    title: 'Carrot Cake with Almond Flour',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 185,
    ingredients: [
      { name: 'almond flour', amount: 2, unit: 'cup' },
      { name: 'carrots', amount: 2, unit: 'cup', notes: 'grated (about 3-4 medium carrots)' },
      { name: 'eggs', amount: 3, unit: 'piece', notes: 'large' },
      { name: 'coconut oil', amount: 0.33, unit: 'cup', notes: 'melted' },
      { name: 'maple syrup', amount: 0.25, unit: 'cup' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'cinnamon', amount: 1, unit: 'tsp', notes: 'ground' },
      { name: 'ginger', amount: 0.5, unit: 'tsp', notes: 'ground' },
      { name: 'nutmeg', amount: 0.25, unit: 'tsp', notes: 'ground' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'baking soda', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' },
      { name: 'walnuts', amount: 0.5, unit: 'cup', notes: 'chopped, optional' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Grease a 9x9 inch baking pan or line with parchment paper.',
      'Grate carrots using a box grater.',
      'In a large bowl, whisk together eggs, melted coconut oil, maple syrup, and vanilla extract.',
      'In a separate bowl, mix almond flour, cinnamon, ginger, nutmeg, baking powder, baking soda, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Fold in grated carrots and walnuts if using.',
      'Pour batter into prepared pan and spread evenly.',
      'Bake for 30-35 minutes, or until a toothpick inserted into the center comes out clean.',
      'Let cool completely before cutting into squares.',
      'Store in an airtight container at room temperature for up to 3 days.'
    ],
    notes: 'Made with almond flour instead of white flour, naturally sweetened with carrots and minimal added sugar. Lower glycemic.'
  },

  'zucchini-bread-with-whole-grains': {
    title: 'Zucchini Bread with Whole Grains',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 180,
    ingredients: [
      { name: 'zucchini', amount: 2, unit: 'cup', notes: 'grated (about 2 medium zucchinis)' },
      { name: 'whole wheat flour', amount: 2, unit: 'cup' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'coconut oil', amount: 0.33, unit: 'cup', notes: 'melted' },
      { name: 'maple syrup', amount: 0.25, unit: 'cup' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'cinnamon', amount: 1, unit: 'tsp', notes: 'ground' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'baking soda', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Grease a 9x5 inch loaf pan.',
      'Grate zucchini using a box grater. Place in a clean kitchen towel and squeeze out excess moisture.',
      'In a large bowl, whisk together eggs, melted coconut oil, maple syrup, and vanilla extract.',
      'In a separate bowl, mix whole wheat flour, cinnamon, baking powder, baking soda, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Fold in grated zucchini.',
      'Pour batter into prepared pan.',
      'Bake for 50-60 minutes, or until a toothpick inserted into the center comes out clean.',
      'Let cool in pan for 10 minutes, then transfer to a wire rack.',
      'Store in an airtight container at room temperature for up to 3 days.'
    ],
    notes: 'Made with whole wheat flour and grated zucchini, naturally moist with less oil. Higher fiber, lower sugar.'
  },

  'pumpkin-spice-cake-with-oat-flour': {
    title: 'Pumpkin Spice Cake with Oat Flour',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 190,
    ingredients: [
      { name: 'oat flour', amount: 2, unit: 'cup', notes: 'or grind 2.5 cups rolled oats' },
      { name: 'pumpkin puree', amount: 1, unit: 'can', notes: '15 oz, or 1.75 cups homemade' },
      { name: 'eggs', amount: 3, unit: 'piece', notes: 'large' },
      { name: 'coconut oil', amount: 0.33, unit: 'cup', notes: 'melted' },
      { name: 'maple syrup', amount: 0.33, unit: 'cup' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'cinnamon', amount: 1, unit: 'tsp', notes: 'ground' },
      { name: 'ginger', amount: 0.5, unit: 'tsp', notes: 'ground' },
      { name: 'nutmeg', amount: 0.25, unit: 'tsp', notes: 'ground' },
      { name: 'cloves', amount: 0.125, unit: 'tsp', notes: 'ground' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'baking soda', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Grease a 9x9 inch baking pan or line with parchment paper.',
      'If making oat flour: Grind 2.5 cups rolled oats in a food processor until fine.',
      'In a large bowl, whisk together pumpkin puree, eggs, melted coconut oil, maple syrup, and vanilla extract.',
      'In a separate bowl, mix oat flour, cinnamon, ginger, nutmeg, cloves, baking powder, baking soda, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Pour batter into prepared pan and spread evenly.',
      'Bake for 35-40 minutes, or until a toothpick inserted into the center comes out clean.',
      'Let cool completely before cutting into squares.',
      'Store in an airtight container at room temperature for up to 3 days.'
    ],
    notes: 'Made with oat flour, naturally sweetened with pumpkin and spices. Lower glycemic, high in Vitamin A.'
  },

  'apple-cinnamon-cake-with-whole-wheat': {
    title: 'Apple Cinnamon Cake with Whole Wheat',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 175,
    ingredients: [
      { name: 'apples', amount: 2, unit: 'piece', notes: 'medium, peeled and grated' },
      { name: 'whole wheat flour', amount: 2, unit: 'cup' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'coconut oil', amount: 0.33, unit: 'cup', notes: 'melted' },
      { name: 'maple syrup', amount: 0.25, unit: 'cup' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'cinnamon', amount: 1.5, unit: 'tsp', notes: 'ground' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'baking soda', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Grease a 9x9 inch baking pan or line with parchment paper.',
      'Peel and grate apples using a box grater.',
      'In a large bowl, whisk together eggs, melted coconut oil, maple syrup, and vanilla extract.',
      'In a separate bowl, mix whole wheat flour, cinnamon, baking powder, baking soda, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Fold in grated apples.',
      'Pour batter into prepared pan and spread evenly.',
      'Bake for 35-40 minutes, or until a toothpick inserted into the center comes out clean.',
      'Let cool completely before cutting into squares.',
      'Store in an airtight container at room temperature for up to 3 days.'
    ],
    notes: 'Made with whole wheat flour, naturally sweetened with apples and cinnamon. No refined sugar, high fiber.'
  },

  'chocolate-avocado-cake-dairy-free': {
    title: 'Chocolate Avocado Cake (Dairy-Free)',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 205,
    ingredients: [
      { name: 'avocado', amount: 1, unit: 'piece', notes: 'large, ripe' },
      { name: 'almond flour', amount: 1.5, unit: 'cup' },
      { name: 'dark chocolate', amount: 4, unit: 'oz', notes: '70% cacao or higher, melted' },
      { name: 'coconut oil', amount: 0.25, unit: 'cup', notes: 'melted' },
      { name: 'maple syrup', amount: 0.33, unit: 'cup' },
      { name: 'eggs', amount: 3, unit: 'piece', notes: 'large' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'cocoa powder', amount: 0.5, unit: 'cup', notes: 'unsweetened' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'baking soda', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Grease a 9x9 inch baking pan or line with parchment paper.',
      'Mash avocado until smooth.',
      'Melt dark chocolate and coconut oil together.',
      'In a large bowl, whisk together mashed avocado, melted chocolate, maple syrup, eggs, and vanilla extract.',
      'In a separate bowl, mix almond flour, cocoa powder, baking powder, baking soda, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Pour batter into prepared pan and spread evenly.',
      'Bake for 30-35 minutes, or until a toothpick inserted into the center comes out clean.',
      'Let cool completely before cutting into squares.',
      'Store in an airtight container at room temperature for up to 3 days.'
    ],
    notes: 'Made with avocado for healthy fats and moisture, almond flour, and dark chocolate. Dairy-free, lower sugar.'
  },

  'sweet-potato-cake-with-spices': {
    title: 'Sweet Potato Cake with Spices',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 185,
    ingredients: [
      { name: 'sweet potato', amount: 1.5, unit: 'cup', notes: 'cooked and mashed' },
      { name: 'whole wheat flour', amount: 2, unit: 'cup' },
      { name: 'eggs', amount: 3, unit: 'piece', notes: 'large' },
      { name: 'coconut oil', amount: 0.33, unit: 'cup', notes: 'melted' },
      { name: 'maple syrup', amount: 0.25, unit: 'cup' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'cinnamon', amount: 1, unit: 'tsp', notes: 'ground' },
      { name: 'ginger', amount: 0.5, unit: 'tsp', notes: 'ground' },
      { name: 'nutmeg', amount: 0.25, unit: 'tsp', notes: 'ground' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'baking soda', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Grease a 9x9 inch baking pan or line with parchment paper.',
      'Cook and mash sweet potato until smooth. Measure 1.5 cups.',
      'In a large bowl, whisk together mashed sweet potato, eggs, melted coconut oil, maple syrup, and vanilla extract.',
      'In a separate bowl, mix whole wheat flour, cinnamon, ginger, nutmeg, baking powder, baking soda, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Pour batter into prepared pan and spread evenly.',
      'Bake for 35-40 minutes, or until a toothpick inserted into the center comes out clean.',
      'Let cool completely before cutting into squares.',
      'Store in an airtight container at room temperature for up to 3 days.'
    ],
    notes: 'Made with sweet potato puree, whole wheat flour, and spices. Naturally sweet, high in beta-carotene.'
  },

  'date-sweetened-spice-cake': {
    title: 'Date-Sweetened Spice Cake',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 180,
    ingredients: [
      { name: 'dates', amount: 1.5, unit: 'cup', notes: 'pitted, medjool dates preferred' },
      { name: 'whole wheat flour', amount: 2, unit: 'cup' },
      { name: 'eggs', amount: 3, unit: 'piece', notes: 'large' },
      { name: 'coconut oil', amount: 0.5, unit: 'cup', notes: 'melted' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'cinnamon', amount: 1, unit: 'tsp', notes: 'ground' },
      { name: 'nutmeg', amount: 0.5, unit: 'tsp', notes: 'ground' },
      { name: 'ginger', amount: 0.5, unit: 'tsp', notes: 'ground' },
      { name: 'allspice', amount: 0.25, unit: 'tsp', notes: 'ground' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'baking soda', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' },
      { name: 'walnuts', amount: 0.5, unit: 'cup', notes: 'chopped, optional' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Grease a 9x9 inch baking pan or line with parchment paper.',
      'Soak dates in hot water for 15 minutes to soften. Drain and reserve 3 tbsp of the soaking water.',
      'In a food processor, blend soaked dates with reserved water until a smooth paste forms.',
      'In a large bowl, whisk together date paste, melted coconut oil, eggs, and vanilla extract until smooth.',
      'In a separate bowl, mix whole wheat flour, cinnamon, nutmeg, ginger, allspice, baking powder, baking soda, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined. Fold in walnuts if using.',
      'Pour batter into prepared pan and spread evenly.',
      'Bake for 30-35 minutes, or until a toothpick inserted into the center comes out clean.',
      'Let cool completely in the pan before cutting into squares.',
      'Store in an airtight container at room temperature for up to 3 days, or refrigerate for up to 1 week.',
      'Dates provide natural sweetness, fiber, and minerals (potassium, magnesium). No refined sugar is needed. Whole grain flour adds fiber and nutrients.'
    ],
    notes: 'Dates provide natural sweetness, fiber, and minerals (potassium, magnesium). No refined sugar is needed. Whole grain flour adds fiber and nutrients. This cake is naturally sweetened, high in fiber, and rich in minerals.'
  },

  // ============================================
  // COOKIE RECIPES
  // ============================================
  'banana-oatmeal-3-ingredient-cookies': {
    title: 'Banana Oatmeal 3-Ingredient Cookies',
    cuisine: 'American',
    servings: 8,
    caloriesPerServing: 85,
    ingredients: [
      { name: 'ripe bananas', amount: 2, unit: 'piece', notes: 'medium, very ripe (brown spots)' },
      { name: 'rolled oats', amount: 1, unit: 'cup', notes: 'old-fashioned or quick oats' },
      { name: 'dark chocolate chips', amount: 0.25, unit: 'cup', notes: 'dairy-free, optional' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Line a baking sheet with parchment paper.',
      'In a medium bowl, mash the ripe bananas with a fork until smooth and creamy.',
      'Mix in the rolled oats until well combined. The mixture should be thick and slightly sticky.',
      'If using, fold in the dark chocolate chips.',
      'Scoop spoonfuls (about 1-2 tbsp each) onto the prepared baking sheet. Flatten slightly with the back of a spoon.',
      'Bake for 12-15 minutes until edges are golden and cookies are set.',
      'Let cool on the baking sheet for 5 minutes before transferring to a wire rack.',
      'Store in an airtight container. These cookies are soft and chewy, similar to traditional oatmeal cookies but without dairy or refined sugar.'
    ],
    notes: 'These cookies are naturally sweetened by the bananas, which also provide moisture and binding. No eggs, butter, or refined sugar needed. The oats add fiber and have a lower glycemic impact than white flour.'
  },

  'almond-flour-chocolate-chip-cookies-dairy-free': {
    title: 'Almond Flour Chocolate Chip Cookies (Dairy-Free)',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 110,
    ingredients: [
      { name: 'almond flour', amount: 1.5, unit: 'cup', notes: 'blanched almond flour' },
      { name: 'maple syrup', amount: 0.25, unit: 'cup', notes: 'pure maple syrup' },
      { name: 'coconut oil', amount: 0.25, unit: 'cup', notes: 'melted' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'baking soda', amount: 0.5, unit: 'tsp' },
      { name: 'dark chocolate chips', amount: 0.25, unit: 'cup', notes: 'dairy-free' },
      { name: 'salt', amount: 0.125, unit: 'tsp', notes: 'pinch' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Line a baking sheet with parchment paper.',
      'In a medium bowl, mix together almond flour, baking soda, and salt.',
      'In a separate bowl, whisk together melted coconut oil, maple syrup, and vanilla extract.',
      'Combine wet and dry ingredients, mixing until a dough forms.',
      'Fold in the dark chocolate chips.',
      'Scoop tablespoon-sized portions onto the baking sheet, spacing them about 2 inches apart.',
      'Bake for 10-12 minutes until edges are golden brown. The centers may look slightly soft but will firm up as they cool.',
      'Let cool on the baking sheet for 5 minutes before transferring to a wire rack.',
      'Store in an airtight container. These cookies have a chewy, soft texture similar to regular cookies but with healthier fats and lower glycemic impact.'
    ],
    notes: 'Almond flour is naturally low in carbs and has a lower glycemic index than wheat flour. Coconut oil provides healthy fats, and maple syrup is less processed than white sugar.'
  },

  'apple-cinnamon-soft-cookies': {
    title: 'Apple Cinnamon Soft Cookies',
    cuisine: 'American',
    servings: 8,
    caloriesPerServing: 75,
    ingredients: [
      { name: 'apple', amount: 1, unit: 'piece', notes: 'medium, finely chopped' },
      { name: 'rolled oats', amount: 1, unit: 'cup', notes: 'old-fashioned or quick oats' },
      { name: 'almond butter', amount: 1, unit: 'tbsp', notes: 'or peanut butter' },
      { name: 'cinnamon', amount: 1, unit: 'tsp', notes: 'ground' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Line a baking sheet with parchment paper.',
      'Finely chop the apple into small pieces (about ¼-inch cubes).',
      'Place chopped apple in a microwave-safe bowl and microwave for 1 minute to soften.',
      'In a medium bowl, combine the softened apple, rolled oats, almond butter, cinnamon, and vanilla extract. Mix until well combined.',
      'Form the mixture into small cookie shapes (about 1-2 tbsp each) and place on the prepared baking sheet.',
      'Flatten slightly with the back of a spoon or your hand.',
      'Bake for 15 minutes until cookies are set and slightly golden.',
      'Let cool on the baking sheet for 5 minutes before serving.',
      'Store in an airtight container. These cookies are naturally sweetened by the apple and have no added sugar.'
    ],
    notes: 'Apples provide natural sweetness and fiber. Cinnamon has anti-inflammatory properties. These cookies are very low in added sugars and use whole-food ingredients.'
  },

  'greek-yogurt-frozen-cookie-bites-high-protein': {
    title: 'Greek Yogurt Frozen Cookie Bites (High Protein)',
    cuisine: 'American',
    servings: 8,
    caloriesPerServing: 95,
    ingredients: [
      { name: 'Greek yogurt', amount: 0.5, unit: 'cup', notes: 'plain, full-fat or low-fat' },
      { name: 'almond butter', amount: 2, unit: 'tbsp' },
      { name: 'dark chocolate chips', amount: 0.25, unit: 'cup', notes: 'dairy-free' },
      { name: 'maple syrup', amount: 1, unit: 'tbsp', notes: 'optional, for sweetness' },
      { name: 'vanilla extract', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'In a medium bowl, mix Greek yogurt, almond butter, maple syrup (if using), and vanilla extract until smooth.',
      'Fold in dark chocolate chips.',
      'Scoop tablespoon-sized portions onto a parchment-lined baking sheet or plate.',
      'Freeze for at least 2 hours until firm.',
      'Store in an airtight container in the freezer.',
      'These can be eaten frozen or thawed slightly for a softer texture.'
    ],
    notes: 'Lower sugar, high protein, frozen = no baking needed. Made with Greek yogurt, almond butter, and dark chocolate chips.'
  },

  'pumpkin-oat-cookies-anti-inflammatory': {
    title: 'Pumpkin Oat Cookies (Anti-Inflammatory)',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 80,
    ingredients: [
      { name: 'pumpkin puree', amount: 0.5, unit: 'cup' },
      { name: 'rolled oats', amount: 1, unit: 'cup' },
      { name: 'maple syrup', amount: 0.25, unit: 'cup' },
      { name: 'almond butter', amount: 2, unit: 'tbsp' },
      { name: 'cinnamon', amount: 1, unit: 'tsp', notes: 'ground' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Line a baking sheet with parchment paper.',
      'In a medium bowl, mix pumpkin puree, rolled oats, maple syrup, almond butter, cinnamon, and vanilla extract until well combined.',
      'Form the mixture into small cookie shapes (about 1-2 tbsp each) and place on the prepared baking sheet.',
      'Flatten slightly with the back of a spoon.',
      'Bake for 15-18 minutes until cookies are set and slightly golden.',
      'Let cool on the baking sheet for 5 minutes before serving.',
      'Store in an airtight container. These cookies are naturally sweetened and anti-inflammatory.'
    ],
    notes: 'Pumpkin = Vitamin A (helps skin), oats = low glycemic. Made with pumpkin puree, oats, and maple syrup.'
  },

  'date-and-nut-cookies': {
    title: 'Date and Nut Cookies',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 105,
    ingredients: [
      { name: 'dates', amount: 1, unit: 'cup', notes: 'pitted, medjool dates preferred' },
      { name: 'almonds', amount: 0.5, unit: 'cup', notes: 'or mixed nuts, chopped' },
      { name: 'whole wheat flour', amount: 0.5, unit: 'cup' },
      { name: 'coconut oil', amount: 2, unit: 'tbsp', notes: 'melted' },
      { name: 'vanilla extract', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.25, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Line a baking sheet with parchment paper.',
      'Soak dates in hot water for 10 minutes to soften. Drain.',
      'In a food processor, blend dates, almonds, whole wheat flour, coconut oil, vanilla, and salt until a dough forms.',
      'Form the mixture into small cookie shapes (about 1-2 tbsp each) and place on the prepared baking sheet.',
      'Flatten slightly with the back of a spoon.',
      'Bake for 12-15 minutes until cookies are set and slightly golden.',
      'Let cool on the baking sheet for 5 minutes before serving.',
      'Store in an airtight container. These cookies are naturally sweetened with dates and have no refined sugar.'
    ],
    notes: 'Naturally sweetened with dates, made with nuts and whole grain flour. No refined sugar, high healthy fats.'
  },

  'oatmeal-raisin-cookies-whole-grain': {
    title: 'Oatmeal Raisin Cookies (Whole Grain)',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 115,
    ingredients: [
      { name: 'rolled oats', amount: 1.5, unit: 'cup', notes: 'old-fashioned' },
      { name: 'whole wheat flour', amount: 0.5, unit: 'cup' },
      { name: 'raisins', amount: 0.5, unit: 'cup' },
      { name: 'coconut oil', amount: 0.25, unit: 'cup', notes: 'melted' },
      { name: 'maple syrup', amount: 0.25, unit: 'cup' },
      { name: 'eggs', amount: 1, unit: 'piece', notes: 'large' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'cinnamon', amount: 0.5, unit: 'tsp', notes: 'ground' },
      { name: 'baking soda', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.25, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Line a baking sheet with parchment paper.',
      'In a medium bowl, mix rolled oats, whole wheat flour, cinnamon, baking soda, and salt.',
      'In a separate bowl, whisk together melted coconut oil, maple syrup, egg, and vanilla extract.',
      'Combine wet and dry ingredients. Fold in raisins.',
      'Scoop tablespoon-sized portions onto the baking sheet, spacing them about 2 inches apart.',
      'Bake for 12-15 minutes until edges are golden brown.',
      'Let cool on the baking sheet for 5 minutes before transferring to a wire rack.',
      'Store in an airtight container. These cookies are higher in fiber than regular cookies.'
    ],
    notes: 'Made with whole grain oats, minimal added sugar, and raisins for natural sweetness. Higher fiber than regular cookies.'
  },

  'coconut-flour-cookies': {
    title: 'Coconut Flour Cookies',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 100,
    ingredients: [
      { name: 'coconut flour', amount: 0.5, unit: 'cup' },
      { name: 'coconut oil', amount: 0.25, unit: 'cup', notes: 'melted' },
      { name: 'maple syrup', amount: 0.25, unit: 'cup' },
      { name: 'eggs', amount: 3, unit: 'piece', notes: 'large' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'dark chocolate chips', amount: 0.25, unit: 'cup', notes: 'optional' },
      { name: 'salt', amount: 0.25, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Line a baking sheet with parchment paper.',
      'In a medium bowl, mix coconut flour and salt.',
      'In a separate bowl, whisk together melted coconut oil, maple syrup, eggs, and vanilla extract.',
      'Combine wet and dry ingredients. Let sit for 5 minutes (coconut flour needs time to absorb liquid).',
      'Fold in dark chocolate chips if using.',
      'Scoop tablespoon-sized portions onto the baking sheet, spacing them about 2 inches apart.',
      'Bake for 12-15 minutes until edges are golden brown.',
      'Let cool on the baking sheet for 5 minutes before transferring to a wire rack.',
      'Store in an airtight container. These cookies are naturally gluten-free and lower in carbs.'
    ],
    notes: 'Made with coconut flour, naturally gluten-free and lower in carbs. Uses healthy fats from coconut.'
  },

  // ============================================
  // PIZZA RECIPES (Added from previous conversation)
  // ============================================
  'italian-thin-crust-veggie-pizza-light-cheese': {
    title: 'Thin-Crust Veggie Pizza with Light Cheese',
    cuisine: 'Italian',
    servings: 4,
    caloriesPerServing: 280,
    ingredients: [
      { name: 'whole-wheat flour', amount: 2, unit: 'cup' },
      { name: 'active dry yeast', amount: 1, unit: 'tsp' },
      { name: 'warm water', amount: 0.75, unit: 'cup' },
      { name: 'olive oil', amount: 2, unit: 'tbsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' },
      { name: 'tomato sauce', amount: 0.5, unit: 'cup', notes: 'no added sugar' },
      { name: 'mozzarella cheese', amount: 0.5, unit: 'cup', notes: 'part-skim, shredded' },
      { name: 'bell peppers', amount: 1, unit: 'piece', notes: 'sliced' },
      { name: 'mushrooms', amount: 4, unit: 'oz', notes: 'sliced' },
      { name: 'zucchini', amount: 1, unit: 'piece', notes: 'thinly sliced' },
      { name: 'red onion', amount: 0.5, unit: 'piece', notes: 'thinly sliced' },
      { name: 'dried oregano', amount: 1, unit: 'tsp' },
      { name: 'fresh basil', amount: 2, unit: 'tbsp', notes: 'chopped, for garnish' }
    ],
    steps: [
      'Dissolve yeast in warm water and let sit for 5 minutes until foamy.',
      'Mix whole-wheat flour, salt, and olive oil in a bowl. Add yeast mixture and knead for 5-7 minutes until smooth.',
      'Place dough in an oiled bowl, cover, and let rise for 1 hour in a warm place.',
      'Preheat oven to 450°F (230°C). Roll out dough on a floured surface into a thin circle (about 12 inches).',
      'Place on a pizza pan or baking sheet. Pre-bake crust for 5 minutes.',
      'Spread tomato sauce evenly over crust. Sprinkle with half the cheese.',
      'Top with bell peppers, mushrooms, zucchini, and red onion. Sprinkle with remaining cheese and oregano.',
      'Bake for 12-15 minutes until crust is golden and cheese is melted.',
      'Garnish with fresh basil before serving.'
    ],
    notes: 'Thin crust reduces refined carbs. Using part-skim mozzarella and loading up on vegetables makes this much lower in dairy and higher in fiber than traditional pizza.'
  },

  'italian-cauliflower-crust-pizza': {
    title: 'Cauliflower Crust Pizza',
    cuisine: 'Italian',
    servings: 4,
    caloriesPerServing: 240,
    ingredients: [
      { name: 'cauliflower', amount: 1, unit: 'head', notes: 'medium, riced (about 3 cups)' },
      { name: 'egg', amount: 1, unit: 'piece', notes: 'beaten' },
      { name: 'mozzarella cheese', amount: 0.5, unit: 'cup', notes: 'part-skim, shredded' },
      { name: 'parmesan cheese', amount: 0.25, unit: 'cup', notes: 'grated' },
      { name: 'dried oregano', amount: 1, unit: 'tsp' },
      { name: 'garlic powder', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.25, unit: 'tsp' },
      { name: 'tomato sauce', amount: 0.5, unit: 'cup', notes: 'no added sugar' },
      { name: 'mozzarella cheese', amount: 0.5, unit: 'cup', notes: 'part-skim, for topping' },
      { name: 'bell peppers', amount: 1, unit: 'piece', notes: 'sliced' },
      { name: 'mushrooms', amount: 4, unit: 'oz', notes: 'sliced' },
      { name: 'fresh basil', amount: 2, unit: 'tbsp', notes: 'chopped' }
    ],
    steps: [
      'Preheat oven to 400°F (200°C). Rice cauliflower using a food processor or grater.',
      'Steam riced cauliflower for 5-6 minutes until tender. Let cool, then squeeze out excess moisture using a clean kitchen towel.',
      'Mix cauliflower, beaten egg, ½ cup mozzarella, parmesan, oregano, garlic powder, and salt in a bowl.',
      'Form into a pizza crust shape on a parchment-lined baking sheet. Bake for 20-25 minutes until golden and firm.',
      'Remove from oven. Spread tomato sauce, then top with remaining mozzarella, bell peppers, and mushrooms.',
      'Bake for another 10-12 minutes until cheese is melted and vegetables are tender.',
      'Garnish with fresh basil before serving.'
    ],
    notes: 'Cauliflower crust is much lower in carbs and higher in fiber than traditional pizza crust. This version uses minimal cheese to keep it acne-friendly.'
  },

  'margherita-pizza-fresh-basil': {
    title: 'Margherita Pizza with Fresh Basil',
    cuisine: 'Italian',
    servings: 4,
    caloriesPerServing: 290,
    ingredients: [
      { name: 'whole wheat pizza dough', amount: 1, unit: 'lb', notes: 'or make your own with whole wheat flour' },
      { name: 'tomato sauce', amount: 0.5, unit: 'cup', notes: 'simple marinara or crushed tomatoes' },
      { name: 'mozzarella cheese', amount: 4, unit: 'oz', notes: 'light/low-fat, shredded' },
      { name: 'fresh basil', amount: 0.25, unit: 'cup', notes: 'chopped' },
      { name: 'olive oil', amount: 1, unit: 'tbsp' },
      { name: 'garlic', amount: 1, unit: 'clove', notes: 'minced, optional' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' },
      { name: 'black pepper', amount: 0.25, unit: 'tsp', notes: 'to taste' }
    ],
    steps: [
      'Preheat oven to 450°F (230°C). If using a pizza stone, place it in the oven while preheating.',
      'Roll out pizza dough on a floured surface to desired thickness (thinner = crispier).',
      'Place dough on a pizza pan or parchment paper.',
      'Spread tomato sauce evenly over the dough, leaving a 1-inch border for the crust.',
      'Sprinkle light mozzarella cheese evenly over the sauce (use less than traditional pizza).',
      'Drizzle with 1 tbsp olive oil. Add minced garlic if using.',
      'Bake for 12-15 minutes until crust is golden and cheese is melted.',
      'Remove from oven and immediately top with fresh basil.',
      'Season with salt and pepper to taste. Slice and serve.',
      'Using light cheese and simple tomato sauce reduces dairy and processed ingredients compared to multi-cheese pizzas.'
    ],
    notes: 'Simple tomato sauce, light mozzarella, and fresh herbs create a less processed pizza than typical pizzas with multiple cheeses. This version is lower in dairy and saturated fat.'
  },

  'whole-wheat-pizza-vegetables': {
    title: 'Whole Wheat Pizza with Vegetables',
    cuisine: 'Italian',
    servings: 4,
    caloriesPerServing: 270,
    ingredients: [
      { name: 'whole wheat pizza dough', amount: 1, unit: 'lb', notes: 'or make your own with whole wheat flour' },
      { name: 'tomato sauce', amount: 0.5, unit: 'cup', notes: 'simple marinara' },
      { name: 'mozzarella cheese', amount: 3, unit: 'oz', notes: 'light/low-fat, shredded (minimal cheese)' },
      { name: 'mixed vegetables', amount: 2, unit: 'cup', notes: 'bell peppers, mushrooms, onions, zucchini, spinach' },
      { name: 'olive oil', amount: 1, unit: 'tbsp' },
      { name: 'garlic', amount: 2, unit: 'clove', notes: 'minced' },
      { name: 'dried oregano', amount: 1, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' }
    ],
    steps: [
      'Preheat oven to 450°F (230°C).',
      'Sauté mixed vegetables in 1 tsp olive oil with garlic until tender-crisp (about 5 minutes).',
      'Roll out pizza dough on a floured surface.',
      'Place dough on a pizza pan or parchment paper.',
      'Spread tomato sauce evenly over the dough, leaving a 1-inch border.',
      'Sprinkle minimal cheese (3 oz instead of 6-8 oz) over the sauce.',
      'Top with sautéed vegetables and sprinkle with oregano.',
      'Drizzle with remaining olive oil.',
      'Bake for 12-15 minutes until crust is golden and cheese is melted.',
      'Season with salt. Slice and serve.',
      'Whole grain crust, minimal cheese, and extra vegetables increase fiber and nutrients while reducing refined carbs and dairy.'
    ],
    notes: 'Whole grain crust, minimal cheese, and extra vegetables increase fiber and nutrients while reducing refined carbs and dairy. This version is much better for skin health than traditional pizza.'
  },

  'portobello-mushroom-pizza': {
    title: 'Portobello Mushroom Pizza',
    cuisine: 'Italian',
    servings: 4,
    caloriesPerServing: 220,
    ingredients: [
      { name: 'portobello mushrooms', amount: 4, unit: 'piece', notes: 'large, stems removed' },
      { name: 'tomato sauce', amount: 0.25, unit: 'cup', notes: 'simple marinara' },
      { name: 'mozzarella cheese', amount: 2, unit: 'oz', notes: 'light/low-fat, shredded' },
      { name: 'mixed vegetables', amount: 1, unit: 'cup', notes: 'bell peppers, onions, spinach' },
      { name: 'olive oil', amount: 1, unit: 'tbsp' },
      { name: 'garlic', amount: 2, unit: 'clove', notes: 'minced' },
      { name: 'dried oregano', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.25, unit: 'tsp', notes: 'to taste' }
    ],
    steps: [
      'Preheat oven to 400°F (200°C).',
      'Clean portobello mushrooms and remove stems. Brush both sides with olive oil.',
      'Place mushrooms gill-side up on a baking sheet. Bake for 5 minutes to partially cook.',
      'Sauté mixed vegetables in 1 tsp olive oil with garlic until tender.',
      'Remove mushrooms from oven. Fill each mushroom cap with tomato sauce.',
      'Top with sautéed vegetables and sprinkle with light cheese.',
      'Sprinkle with oregano. Return to oven and bake for 8-10 minutes until cheese is melted.',
      'Season with salt. Serve immediately.',
      'Portobello mushroom caps as the base eliminate refined carbs entirely while providing fiber and nutrients.'
    ],
    notes: 'Portobello mushroom caps as the base eliminate refined carbs entirely while providing fiber and nutrients. This version is much lower in carbs and higher in fiber than traditional pizza.'
  },

  'zucchini-crust-pizza': {
    title: 'Zucchini Crust Pizza',
    cuisine: 'Italian',
    servings: 4,
    caloriesPerServing: 200,
    ingredients: [
      { name: 'zucchini', amount: 3, unit: 'cup', notes: 'grated (about 3 medium zucchinis)' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'almond flour', amount: 0.5, unit: 'cup' },
      { name: 'mozzarella cheese', amount: 0.5, unit: 'cup', notes: 'shredded, for crust binding' },
      { name: 'salt', amount: 0.5, unit: 'tsp' },
      { name: 'tomato sauce', amount: 0.5, unit: 'cup', notes: 'simple marinara' },
      { name: 'mozzarella cheese', amount: 2, unit: 'oz', notes: 'light/low-fat, for topping' },
      { name: 'mixed vegetables', amount: 1, unit: 'cup', notes: 'bell peppers, mushrooms, onions' },
      { name: 'olive oil', amount: 1, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 400°F (200°C). Line a baking sheet with parchment paper.',
      'Grate zucchini using a box grater. Place in a clean kitchen towel and squeeze out ALL excess moisture (very important).',
      'In a bowl, mix squeezed zucchini, eggs, almond flour, 0.5 cup shredded cheese, and salt.',
      'Spread the mixture onto the prepared baking sheet in a thin, even circle (about 12 inches).',
      'Bake for 15-20 minutes until the crust is firm and slightly golden.',
      'Remove from oven. Spread tomato sauce over the crust.',
      'Top with mixed vegetables and sprinkle with light cheese.',
      'Return to oven and bake for 8-10 minutes until cheese is melted.',
      'Drizzle with olive oil. Slice and serve.',
      'Zucchini crust is much lower in carbs and higher in fiber than traditional pizza crust.'
    ],
    notes: 'Zucchini crust is much lower in carbs and higher in fiber than traditional pizza crust. This version is excellent for skin health due to low glycemic impact and high vegetable content.'
  },

  'flatbread-pizza-hummus-base': {
    title: 'Flatbread Pizza with Hummus Base',
    cuisine: 'Mediterranean',
    servings: 4,
    caloriesPerServing: 195,
    ingredients: [
      { name: 'whole grain flatbread', amount: 4, unit: 'piece', notes: 'or whole wheat pita bread' },
      { name: 'hummus', amount: 0.5, unit: 'cup', notes: 'store-bought or homemade' },
      { name: 'mixed vegetables', amount: 2, unit: 'cup', notes: 'bell peppers, red onions, cherry tomatoes, spinach' },
      { name: 'feta cheese', amount: 2, unit: 'oz', notes: 'crumbled, optional' },
      { name: 'olive oil', amount: 1, unit: 'tbsp' },
      { name: 'lemon juice', amount: 1, unit: 'tbsp', notes: 'fresh' },
      { name: 'dried oregano', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.25, unit: 'tsp', notes: 'to taste' }
    ],
    steps: [
      'Preheat oven to 400°F (200°C).',
      'Place flatbreads on a baking sheet.',
      'Spread hummus evenly over each flatbread (about 2 tbsp per flatbread).',
      'Top with mixed vegetables (sliced bell peppers, red onions, halved cherry tomatoes, spinach).',
      'If using, sprinkle with crumbled feta cheese (use sparingly).',
      'Drizzle with olive oil and lemon juice. Sprinkle with oregano.',
      'Bake for 8-10 minutes until vegetables are tender and flatbread is crispy.',
      'Season with salt. Serve immediately.',
      'Hummus provides protein and healthy fats as a base instead of tomato sauce, reducing acidity and increasing nutrients.'
    ],
    notes: 'Hummus provides protein and healthy fats as a base instead of tomato sauce, reducing acidity and increasing nutrients. Whole grain flatbread and vegetables make this higher in protein and fiber than traditional pizza.'
  },

  'sweet-potato-crust-pizza': {
    title: 'Sweet Potato Crust Pizza',
    cuisine: 'Italian',
    servings: 4,
    caloriesPerServing: 250,
    ingredients: [
      { name: 'sweet potato', amount: 2, unit: 'cup', notes: 'cooked and mashed (about 2 medium sweet potatoes)' },
      { name: 'whole wheat flour', amount: 1, unit: 'cup' },
      { name: 'eggs', amount: 1, unit: 'piece', notes: 'large' },
      { name: 'olive oil', amount: 1, unit: 'tbsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' },
      { name: 'tomato sauce', amount: 0.5, unit: 'cup', notes: 'simple marinara' },
      { name: 'mozzarella cheese', amount: 2, unit: 'oz', notes: 'light/low-fat, shredded' },
      { name: 'mixed vegetables', amount: 1.5, unit: 'cup', notes: 'bell peppers, mushrooms, onions, spinach' },
      { name: 'dried oregano', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 400°F (200°C).',
      'Cook sweet potatoes: Pierce with fork, bake at 400°F for 45-60 minutes until tender, or microwave for 8-10 minutes. Let cool, peel, and mash until smooth. Measure 2 cups.',
      'In a bowl, mix mashed sweet potato, whole wheat flour, egg, olive oil, and salt until a dough forms.',
      'Roll out dough on a floured surface to desired thickness. Place on a pizza pan or parchment paper.',
      'Bake crust for 10 minutes until slightly firm.',
      'Remove from oven. Spread tomato sauce over the crust.',
      'Top with mixed vegetables and sprinkle with light cheese.',
      'Sprinkle with oregano. Return to oven and bake for 10-12 minutes until cheese is melted.',
      'Slice and serve.',
      'Sweet potato adds beta-carotene and fiber to the crust, making it more nutritious than traditional pizza crust.'
    ],
    notes: 'Sweet potato adds beta-carotene and fiber to the crust, making it more nutritious than traditional pizza crust. This version is higher in fiber and lower in refined carbs than traditional pizza.'
  },

  // ============================================
  // BURGER RECIPES
  // ============================================
  'grilled-chicken-turkey-burger-whole-grain-bun': {
    title: 'Grilled Chicken or Turkey Burger on Whole-Grain Bun',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 350,
    ingredients: [
      { name: 'ground chicken or turkey', amount: 1, unit: 'lb', notes: 'lean ground meat' },
      { name: 'onion', amount: 0.25, unit: 'piece', notes: 'finely chopped' },
      { name: 'garlic', amount: 2, unit: 'clove', notes: 'minced' },
      { name: 'breadcrumbs', amount: 0.25, unit: 'cup', notes: 'whole wheat breadcrumbs' },
      { name: 'egg', amount: 1, unit: 'piece', notes: 'large' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' },
      { name: 'black pepper', amount: 0.25, unit: 'tsp' },
      { name: 'whole grain buns', amount: 4, unit: 'piece' },
      { name: 'lettuce', amount: 4, unit: 'leaf', notes: 'for serving' },
      { name: 'tomato', amount: 1, unit: 'piece', notes: 'sliced, for serving' },
      { name: 'onion', amount: 0.5, unit: 'piece', notes: 'sliced, for serving' },
      { name: 'side salad', amount: 4, unit: 'serving', notes: 'mixed greens, for serving' }
    ],
    steps: [
      'In a large bowl, mix ground chicken/turkey, finely chopped onion, garlic, breadcrumbs, egg, salt, and pepper until well combined.',
      'Form into 4 equal patties (about 4 oz each).',
      'Heat grill or grill pan to medium-high heat. Lightly oil the grates.',
      'Grill patties for 5-6 minutes per side until cooked through (internal temperature 165°F/74°C).',
      'While patties are cooking, prepare side salad with mixed greens.',
      'Toast whole grain buns if desired.',
      'Assemble burgers: Place patty on bottom bun, top with lettuce, tomato, and onion slices.',
      'Serve with side salad instead of fries.',
      'Leaner protein and whole grain buns reduce processed meat and refined carbs compared to traditional beef burgers.'
    ],
    notes: 'Leaner protein (chicken/turkey) and whole grain buns reduce processed meat and refined carbs compared to traditional beef burgers. Serving with salad instead of fries further reduces refined carbs and increases fiber.'
  },

  'veggie-burger-avocado': {
    title: 'Veggie Burger with Avocado',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 320,
    ingredients: [
      { name: 'black beans', amount: 1.5, unit: 'cup', notes: 'cooked, drained, and rinsed' },
      { name: 'sweet potato', amount: 0.5, unit: 'cup', notes: 'cooked and mashed' },
      { name: 'onion', amount: 0.5, unit: 'piece', notes: 'finely chopped' },
      { name: 'garlic', amount: 2, unit: 'clove', notes: 'minced' },
      { name: 'breadcrumbs', amount: 0.5, unit: 'cup', notes: 'whole wheat breadcrumbs' },
      { name: 'cumin', amount: 1, unit: 'tsp' },
      { name: 'paprika', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' },
      { name: 'olive oil', amount: 1, unit: 'tbsp', notes: 'for cooking' },
      { name: 'whole grain buns', amount: 4, unit: 'piece' },
      { name: 'avocado', amount: 1, unit: 'piece', notes: 'sliced, for serving' },
      { name: 'lettuce', amount: 4, unit: 'leaf', notes: 'for serving' },
      { name: 'tomato', amount: 1, unit: 'piece', notes: 'sliced, for serving' }
    ],
    steps: [
      'In a large bowl, mash black beans with a fork (leave some chunks for texture).',
      'Add mashed sweet potato, chopped onion, garlic, breadcrumbs, cumin, paprika, and salt. Mix until well combined.',
      'Form into 4 equal patties. Refrigerate for 30 minutes to firm up.',
      'Heat 1 tbsp olive oil in a large pan over medium heat.',
      'Cook patties for 4-5 minutes per side until golden and heated through.',
      'Toast whole grain buns if desired.',
      'Assemble burgers: Place patty on bottom bun, top with avocado slices, lettuce, and tomato.',
      'Serve immediately.',
      'Plant-based patty with healthy fats from avocado provides fiber and nutrients without processed meat.'
    ],
    notes: 'Plant-based patty with healthy fats from avocado provides fiber and nutrients without processed meat. This version is much better for skin health than traditional beef burgers.'
  },

  'salmon-burger-whole-grain-bun': {
    title: 'Salmon Burger on Whole Grain Bun',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 370,
    ingredients: [
      { name: 'salmon', amount: 1, unit: 'lb', notes: 'fresh, skin removed, cut into chunks' },
      { name: 'onion', amount: 0.25, unit: 'piece', notes: 'finely chopped' },
      { name: 'dill', amount: 1, unit: 'tbsp', notes: 'fresh, chopped' },
      { name: 'lemon juice', amount: 1, unit: 'tbsp', notes: 'fresh' },
      { name: 'breadcrumbs', amount: 0.25, unit: 'cup', notes: 'whole wheat breadcrumbs' },
      { name: 'egg', amount: 1, unit: 'piece', notes: 'large' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' },
      { name: 'black pepper', amount: 0.25, unit: 'tsp' },
      { name: 'olive oil', amount: 1, unit: 'tbsp', notes: 'for cooking' },
      { name: 'whole grain buns', amount: 4, unit: 'piece' },
      { name: 'lettuce', amount: 4, unit: 'leaf', notes: 'for serving' },
      { name: 'tomato', amount: 1, unit: 'piece', notes: 'sliced, for serving' },
      { name: 'red onion', amount: 0.25, unit: 'piece', notes: 'sliced, for serving' }
    ],
    steps: [
      'In a food processor, pulse salmon chunks 3-4 times until coarsely chopped (do not over-process).',
      'Transfer to a bowl. Add chopped onion, dill, lemon juice, breadcrumbs, egg, salt, and pepper. Mix gently.',
      'Form into 4 equal patties. Refrigerate for 30 minutes to firm up.',
      'Heat 1 tbsp olive oil in a large pan over medium heat.',
      'Cook patties for 4-5 minutes per side until golden and cooked through (internal temperature 145°F/63°C).',
      'Toast whole grain buns if desired.',
      'Assemble burgers: Place patty on bottom bun, top with lettuce, tomato, and red onion slices.',
      'Serve immediately.',
      'Salmon provides omega-3 fatty acids which are beneficial for skin health. Whole grain buns add fiber.'
    ],
    notes: 'Salmon provides omega-3 fatty acids which are beneficial for skin health. Whole grain buns add fiber. This version is much better for skin health than traditional beef burgers.'
  },

  'black-bean-burger-whole-grain-bun': {
    title: 'Black Bean Burger with Whole Grain Bun',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 310,
    ingredients: [
      { name: 'black beans', amount: 2, unit: 'cup', notes: 'cooked, drained, and rinsed' },
      { name: 'onion', amount: 0.5, unit: 'piece', notes: 'finely chopped' },
      { name: 'garlic', amount: 2, unit: 'clove', notes: 'minced' },
      { name: 'bell pepper', amount: 0.5, unit: 'piece', notes: 'finely chopped' },
      { name: 'breadcrumbs', amount: 0.5, unit: 'cup', notes: 'whole wheat breadcrumbs' },
      { name: 'cumin', amount: 1, unit: 'tsp' },
      { name: 'chili powder', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' },
      { name: 'olive oil', amount: 1, unit: 'tbsp', notes: 'for cooking' },
      { name: 'whole grain buns', amount: 4, unit: 'piece' },
      { name: 'lettuce', amount: 4, unit: 'leaf', notes: 'for serving' },
      { name: 'tomato', amount: 1, unit: 'piece', notes: 'sliced, for serving' }
    ],
    steps: [
      'In a large bowl, mash black beans with a fork (leave some chunks for texture).',
      'Add chopped onion, garlic, bell pepper, breadcrumbs, cumin, chili powder, and salt. Mix until well combined.',
      'Form into 4 equal patties. Refrigerate for 30 minutes to firm up.',
      'Heat 1 tbsp olive oil in a large pan over medium heat.',
      'Cook patties for 4-5 minutes per side until golden and heated through.',
      'Toast whole grain buns if desired.',
      'Assemble burgers: Place patty on bottom bun, top with lettuce and tomato slices.',
      'Serve immediately.',
      'Black beans provide high fiber and plant protein. No processed meat makes this much better for skin health.'
    ],
    notes: 'Black beans provide high fiber and plant protein. No processed meat makes this much better for skin health than traditional beef burgers.'
  },

  'portobello-mushroom-burger': {
    title: 'Portobello Mushroom Burger',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 280,
    ingredients: [
      { name: 'portobello mushrooms', amount: 4, unit: 'piece', notes: 'large, stems removed' },
      { name: 'balsamic vinegar', amount: 2, unit: 'tbsp' },
      { name: 'olive oil', amount: 2, unit: 'tbsp' },
      { name: 'garlic', amount: 2, unit: 'clove', notes: 'minced' },
      { name: 'dried thyme', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' },
      { name: 'black pepper', amount: 0.25, unit: 'tsp' },
      { name: 'whole grain buns', amount: 4, unit: 'piece' },
      { name: 'lettuce', amount: 4, unit: 'leaf', notes: 'for serving' },
      { name: 'tomato', amount: 1, unit: 'piece', notes: 'sliced, for serving' },
      { name: 'red onion', amount: 0.25, unit: 'piece', notes: 'sliced, for serving' }
    ],
    steps: [
      'Clean portobello mushrooms and remove stems.',
      'In a bowl, whisk together balsamic vinegar, olive oil, garlic, thyme, salt, and pepper.',
      'Marinate mushrooms in the mixture for at least 30 minutes (overnight is best).',
      'Heat grill or grill pan to medium-high heat. Lightly oil the grates.',
      'Grill mushrooms for 4-5 minutes per side until tender and slightly charred.',
      'Toast whole grain buns if desired.',
      'Assemble burgers: Place grilled mushroom on bottom bun, top with lettuce, tomato, and red onion slices.',
      'Serve immediately.',
      'Portobello mushrooms provide a meaty texture without processed meat. Lower carbs and high fiber make this excellent for skin health.'
    ],
    notes: 'Portobello mushrooms provide a meaty texture without processed meat. Lower carbs and high fiber make this excellent for skin health. This version is plant-based and much better than traditional beef burgers.'
  },

  'lentil-burger-whole-grain-bun': {
    title: 'Lentil Burger with Whole Grain Bun',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 305,
    ingredients: [
      { name: 'brown or green lentils', amount: 1.5, unit: 'cup', notes: 'cooked until tender' },
      { name: 'onion', amount: 0.5, unit: 'piece', notes: 'finely chopped' },
      { name: 'garlic', amount: 2, unit: 'clove', notes: 'minced' },
      { name: 'carrot', amount: 0.5, unit: 'piece', notes: 'grated' },
      { name: 'breadcrumbs', amount: 0.5, unit: 'cup', notes: 'whole wheat breadcrumbs' },
      { name: 'cumin', amount: 1, unit: 'tsp' },
      { name: 'coriander', amount: 0.5, unit: 'tsp', notes: 'ground' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' },
      { name: 'olive oil', amount: 1, unit: 'tbsp', notes: 'for cooking' },
      { name: 'whole grain buns', amount: 4, unit: 'piece' },
      { name: 'lettuce', amount: 4, unit: 'leaf', notes: 'for serving' },
      { name: 'tomato', amount: 1, unit: 'piece', notes: 'sliced, for serving' }
    ],
    steps: [
      'Cook lentils until tender (about 20-25 minutes). Drain and let cool slightly.',
      'In a large bowl, mash lentils with a fork (leave some texture).',
      'Add chopped onion, garlic, grated carrot, breadcrumbs, cumin, coriander, and salt. Mix until well combined.',
      'Form into 4 equal patties. Refrigerate for 30 minutes to firm up.',
      'Heat 1 tbsp olive oil in a large pan over medium heat.',
      'Cook patties for 4-5 minutes per side until golden and heated through.',
      'Toast whole grain buns if desired.',
      'Assemble burgers: Place patty on bottom bun, top with lettuce and tomato slices.',
      'Serve immediately.',
      'Lentils provide high protein and fiber. No processed meat makes this much better for skin health.'
    ],
    notes: 'Lentils provide high protein and fiber. No processed meat makes this much better for skin health than traditional beef burgers.'
  },

  'chickpea-burger-whole-grain-bun': {
    title: 'Chickpea Burger with Whole Grain Bun',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 315,
    ingredients: [
      { name: 'chickpeas', amount: 2, unit: 'cup', notes: 'cooked, drained, and rinsed' },
      { name: 'onion', amount: 0.5, unit: 'piece', notes: 'finely chopped' },
      { name: 'garlic', amount: 2, unit: 'clove', notes: 'minced' },
      { name: 'cilantro', amount: 2, unit: 'tbsp', notes: 'fresh, chopped' },
      { name: 'breadcrumbs', amount: 0.5, unit: 'cup', notes: 'whole wheat breadcrumbs' },
      { name: 'cumin', amount: 1, unit: 'tsp' },
      { name: 'paprika', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' },
      { name: 'olive oil', amount: 1, unit: 'tbsp', notes: 'for cooking' },
      { name: 'whole grain buns', amount: 4, unit: 'piece' },
      { name: 'lettuce', amount: 4, unit: 'leaf', notes: 'for serving' },
      { name: 'tomato', amount: 1, unit: 'piece', notes: 'sliced, for serving' }
    ],
    steps: [
      'In a large bowl, mash chickpeas with a fork (leave some chunks for texture).',
      'Add chopped onion, garlic, cilantro, breadcrumbs, cumin, paprika, and salt. Mix until well combined.',
      'Form into 4 equal patties. Refrigerate for 30 minutes to firm up.',
      'Heat 1 tbsp olive oil in a large pan over medium heat.',
      'Cook patties for 4-5 minutes per side until golden and heated through.',
      'Toast whole grain buns if desired.',
      'Assemble burgers: Place patty on bottom bun, top with lettuce and tomato slices.',
      'Serve immediately.',
      'Chickpeas provide high protein and fiber. No processed meat makes this much better for skin health.'
    ],
    notes: 'Chickpeas provide high protein and fiber. No processed meat makes this much better for skin health than traditional beef burgers.'
  },

  'quinoa-burger-whole-grain-bun': {
    title: 'Quinoa Burger with Whole Grain Bun',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 330,
    ingredients: [
      { name: 'quinoa', amount: 1.5, unit: 'cup', notes: 'cooked and cooled' },
      { name: 'black beans', amount: 0.5, unit: 'cup', notes: 'cooked, drained, and rinsed' },
      { name: 'onion', amount: 0.5, unit: 'piece', notes: 'finely chopped' },
      { name: 'garlic', amount: 2, unit: 'clove', notes: 'minced' },
      { name: 'breadcrumbs', amount: 0.5, unit: 'cup', notes: 'whole wheat breadcrumbs' },
      { name: 'cumin', amount: 1, unit: 'tsp' },
      { name: 'chili powder', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' },
      { name: 'olive oil', amount: 1, unit: 'tbsp', notes: 'for cooking' },
      { name: 'whole grain buns', amount: 4, unit: 'piece' },
      { name: 'lettuce', amount: 4, unit: 'leaf', notes: 'for serving' },
      { name: 'tomato', amount: 1, unit: 'piece', notes: 'sliced, for serving' }
    ],
    steps: [
      'Cook quinoa according to package instructions. Let cool completely.',
      'In a large bowl, mash black beans with a fork (leave some texture).',
      'Add cooked quinoa, chopped onion, garlic, breadcrumbs, cumin, chili powder, and salt. Mix until well combined.',
      'Form into 4 equal patties. Refrigerate for 30 minutes to firm up.',
      'Heat 1 tbsp olive oil in a large pan over medium heat.',
      'Cook patties for 4-5 minutes per side until golden and heated through.',
      'Toast whole grain buns if desired.',
      'Assemble burgers: Place patty on bottom bun, top with lettuce and tomato slices.',
      'Serve immediately.',
      'Quinoa provides complete protein. Combined with black beans, this burger is high in protein and fiber, making it excellent for skin health.'
    ],
    notes: 'Quinoa provides complete protein. Combined with black beans, this burger is high in protein and fiber, making it excellent for skin health. No processed meat makes this much better than traditional beef burgers.'
  },

  // ============================================
  // CHIP RECIPES
  // ============================================
  'kale-chips-baked': {
    title: 'Kale Chips (Baked)',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 60,
    ingredients: [
      { name: 'kale', amount: 1, unit: 'bunch', notes: 'about 6-8 cups, stems removed, torn into bite-sized pieces' },
      { name: 'olive oil', amount: 1, unit: 'tbsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' },
      { name: 'garlic powder', amount: 0.5, unit: 'tsp', notes: 'optional' },
      { name: 'nutritional yeast', amount: 1, unit: 'tbsp', notes: 'optional, for cheesy flavor' }
    ],
    steps: [
      'Preheat oven to 300°F (150°C). Line two baking sheets with parchment paper.',
      'Wash and thoroughly dry kale leaves. Remove stems and tear into bite-sized pieces.',
      'In a large bowl, toss kale with olive oil, salt, garlic powder (if using), and nutritional yeast (if using) until evenly coated.',
      'Spread kale in a single layer on baking sheets (do not overcrowd).',
      'Bake for 15-20 minutes, rotating sheets halfway through, until kale is crispy and edges are slightly browned.',
      'Let cool for 5 minutes before serving. Kale chips will continue to crisp as they cool.',
      'Store in an airtight container at room temperature for up to 3 days.',
      'Baked kale chips provide vitamins, fiber, and antioxidants without the oil and calories of potato chips.'
    ],
    notes: 'Baked kale chips provide vitamins, fiber, and antioxidants without the oil and calories of potato chips. This is an excellent acne-friendly snack alternative.'
  },

  'roasted-chickpeas': {
    title: 'Roasted Chickpeas',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 130,
    ingredients: [
      { name: 'chickpeas', amount: 2, unit: 'can', notes: '15 oz each, drained, rinsed, and patted dry' },
      { name: 'olive oil', amount: 1, unit: 'tbsp' },
      { name: 'cumin', amount: 1, unit: 'tsp' },
      { name: 'paprika', amount: 0.5, unit: 'tsp' },
      { name: 'garlic powder', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' },
      { name: 'cayenne pepper', amount: 0.125, unit: 'tsp', notes: 'optional, for heat' }
    ],
    steps: [
      'Preheat oven to 400°F (200°C). Line a baking sheet with parchment paper.',
      'Drain and rinse chickpeas. Pat them very dry with a clean kitchen towel (this is crucial for crispiness).',
      'In a bowl, toss chickpeas with olive oil until evenly coated.',
      'Add cumin, paprika, garlic powder, salt, and cayenne (if using). Toss until spices are evenly distributed.',
      'Spread chickpeas in a single layer on the prepared baking sheet.',
      'Bake for 25-30 minutes, shaking the pan halfway through, until chickpeas are golden and crispy.',
      'Let cool for 5 minutes before serving. They will continue to crisp as they cool.',
      'Store in an airtight container at room temperature for up to 5 days.',
      'Roasted chickpeas provide high fiber and protein, making them a much healthier snack than potato chips.'
    ],
    notes: 'Roasted chickpeas provide high fiber and protein, making them a much healthier snack than potato chips. This is an excellent acne-friendly snack alternative.'
  },

  'baked-sweet-potato-chips': {
    title: 'Baked Sweet Potato Chips',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 110,
    ingredients: [
      { name: 'sweet potatoes', amount: 2, unit: 'piece', notes: 'medium, scrubbed' },
      { name: 'olive oil', amount: 1, unit: 'tbsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' },
      { name: 'paprika', amount: 0.5, unit: 'tsp', notes: 'optional' },
      { name: 'cinnamon', amount: 0.25, unit: 'tsp', notes: 'optional, for sweet version' }
    ],
    steps: [
      'Preheat oven to 375°F (190°C). Line two baking sheets with parchment paper.',
      'Using a mandoline or sharp knife, slice sweet potatoes into very thin rounds (about 1/16 inch thick).',
      'In a large bowl, toss sweet potato slices with olive oil, salt, and paprika (or cinnamon for sweet version) until evenly coated.',
      'Spread slices in a single layer on baking sheets (do not overlap).',
      'Bake for 15-20 minutes, flipping halfway through, until chips are golden and crispy.',
      'Let cool for 5 minutes before serving. Chips will continue to crisp as they cool.',
      'Store in an airtight container at room temperature for up to 3 days.',
      'Baked sweet potato chips are lower in glycemic impact than white potatoes and provide beta-carotene (vitamin A).'
    ],
    notes: 'Baked sweet potato chips are lower in glycemic impact than white potatoes and provide beta-carotene (vitamin A). This is a much healthier alternative to fried potato chips.'
  },

  'baked-zucchini-chips': {
    title: 'Baked Zucchini Chips',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 50,
    ingredients: [
      { name: 'zucchini', amount: 2, unit: 'piece', notes: 'medium, scrubbed' },
      { name: 'olive oil', amount: 1, unit: 'tbsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' },
      { name: 'garlic powder', amount: 0.5, unit: 'tsp', notes: 'optional' },
      { name: 'parmesan cheese', amount: 2, unit: 'tbsp', notes: 'grated, optional' }
    ],
    steps: [
      'Preheat oven to 225°F (110°C). Line two baking sheets with parchment paper.',
      'Using a mandoline or sharp knife, slice zucchini into very thin rounds (about 1/16 inch thick).',
      'In a large bowl, toss zucchini slices with olive oil, salt, and garlic powder (if using) until evenly coated.',
      'Spread slices in a single layer on baking sheets (do not overlap).',
      'If using parmesan, sprinkle a small amount over each slice.',
      'Bake for 1.5-2 hours, flipping halfway through, until chips are golden and crispy.',
      'Let cool for 5 minutes before serving. Chips will continue to crisp as they cool.',
      'Store in an airtight container at room temperature for up to 3 days.',
      'Baked zucchini chips are very low in carbs and high in fiber, making them an excellent alternative to potato chips.'
    ],
    notes: 'Baked zucchini chips are very low in carbs and high in fiber, making them an excellent alternative to potato chips. This is a great acne-friendly snack.'
  },

  'roasted-edamame': {
    title: 'Roasted Edamame',
    cuisine: 'Japanese',
    servings: 4,
    caloriesPerServing: 125,
    ingredients: [
      { name: 'edamame', amount: 2, unit: 'cup', notes: 'frozen, shelled' },
      { name: 'olive oil', amount: 1, unit: 'tbsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' },
      { name: 'garlic powder', amount: 0.5, unit: 'tsp', notes: 'optional' },
      { name: 'sesame seeds', amount: 1, unit: 'tsp', notes: 'optional' }
    ],
    steps: [
      'Preheat oven to 400°F (200°C). Line a baking sheet with parchment paper.',
      'If using frozen edamame, thaw and pat dry with a clean kitchen towel.',
      'In a bowl, toss edamame with olive oil, salt, garlic powder (if using), and sesame seeds (if using) until evenly coated.',
      'Spread edamame in a single layer on the prepared baking sheet.',
      'Bake for 15-20 minutes, shaking the pan halfway through, until edamame is golden and crispy.',
      'Let cool for 5 minutes before serving.',
      'Store in an airtight container at room temperature for up to 5 days.',
      'Roasted edamame provides high protein and fiber, making it an excellent snack alternative to chips.'
    ],
    notes: 'Roasted edamame provides high protein and fiber, making it an excellent snack alternative to chips. This is a great acne-friendly snack.'
  },

  'baked-beet-chips': {
    title: 'Baked Beet Chips',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 90,
    ingredients: [
      { name: 'beets', amount: 2, unit: 'piece', notes: 'medium, scrubbed and peeled' },
      { name: 'olive oil', amount: 1, unit: 'tbsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' },
      { name: 'rosemary', amount: 0.5, unit: 'tsp', notes: 'dried, optional' }
    ],
    steps: [
      'Preheat oven to 375°F (190°C). Line two baking sheets with parchment paper.',
      'Using a mandoline or sharp knife, slice beets into very thin rounds (about 1/16 inch thick).',
      'In a large bowl, toss beet slices with olive oil, salt, and rosemary (if using) until evenly coated.',
      'Spread slices in a single layer on baking sheets (do not overlap).',
      'Bake for 20-25 minutes, flipping halfway through, until chips are crispy and edges are slightly browned.',
      'Let cool for 5 minutes before serving. Chips will continue to crisp as they cool.',
      'Store in an airtight container at room temperature for up to 3 days.',
      'Baked beet chips are high in antioxidants, fiber, and nutrients, making them an excellent alternative to potato chips.'
    ],
    notes: 'Baked beet chips are high in antioxidants, fiber, and nutrients, making them an excellent alternative to potato chips. This is a great acne-friendly snack.'
  },

  // ============================================
  // ICE CREAM RECIPES
  // ============================================
  'frozen-banana-nice-cream': {
    title: 'Frozen Banana "Nice Cream"',
    cuisine: 'American',
    servings: 2,
    caloriesPerServing: 120,
    ingredients: [
      { name: 'bananas', amount: 3, unit: 'piece', notes: 'very ripe, frozen (peel and freeze overnight)' },
      { name: 'milk', amount: 2, unit: 'tbsp', notes: 'almond milk, coconut milk, or regular milk' },
      { name: 'vanilla extract', amount: 0.5, unit: 'tsp', notes: 'optional' }
    ],
    steps: [
      'Peel and slice 3 very ripe bananas. Place in a freezer bag and freeze overnight (at least 8 hours).',
      'Remove frozen bananas from freezer and let sit at room temperature for 5 minutes to slightly soften.',
      'Place frozen banana slices in a food processor or high-speed blender.',
      'Add 2 tbsp milk and vanilla extract (if using).',
      'Blend on high speed, scraping down the sides as needed, until smooth and creamy (about 2-3 minutes).',
      'The mixture should have a soft-serve ice cream consistency.',
      'Serve immediately for soft-serve texture, or freeze for 30 minutes for a firmer texture.',
      'Store any leftovers in an airtight container in the freezer. Let sit at room temperature for 5 minutes before serving.'
    ],
    notes: 'Blended frozen bananas with a splash of milk for a creamy, dairy-light dessert. No added sugar. This is naturally sweet and creamy without any refined sugar or heavy dairy.'
  },

  'coconut-milk-ice-cream': {
    title: 'Coconut Milk Ice Cream',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 180,
    ingredients: [
      { name: 'coconut milk', amount: 2, unit: 'can', notes: '13.5 oz each, full-fat, chilled' },
      { name: 'maple syrup', amount: 0.25, unit: 'cup', notes: 'or honey, to taste' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'salt', amount: 0.125, unit: 'tsp', notes: 'pinch' }
    ],
    steps: [
      'Chill coconut milk cans in the refrigerator overnight.',
      'Open cans and scoop out the thick coconut cream (the solid part that rises to the top).',
      'In a large bowl, whip the coconut cream with an electric mixer until fluffy (about 2-3 minutes).',
      'Add maple syrup, vanilla extract, and a pinch of salt. Mix until well combined.',
      'Pour mixture into a freezer-safe container.',
      'Freeze for at least 4 hours, stirring every hour for the first 2 hours to prevent ice crystals.',
      'Let sit at room temperature for 10 minutes before serving to soften.',
      'Store in an airtight container in the freezer for up to 2 weeks.'
    ],
    notes: 'Made with coconut milk instead of dairy, naturally sweetened. Lower in dairy, healthy fats from coconut.'
  },

  'greek-yogurt-frozen-treat': {
    title: 'Greek Yogurt Frozen Treat',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 140,
    ingredients: [
      { name: 'Greek yogurt', amount: 2, unit: 'cup', notes: 'plain, full-fat or low-fat' },
      { name: 'berries', amount: 1, unit: 'cup', notes: 'fresh or frozen (strawberries, blueberries, raspberries)' },
      { name: 'maple syrup', amount: 2, unit: 'tbsp', notes: 'or honey, to taste' },
      { name: 'vanilla extract', amount: 0.5, unit: 'tsp', notes: 'optional' }
    ],
    steps: [
      'If using fresh berries, wash and dry them. If using frozen berries, let them thaw slightly.',
      'In a blender or food processor, blend berries until smooth.',
      'In a large bowl, mix Greek yogurt, blended berries, maple syrup, and vanilla extract (if using) until well combined.',
      'Pour mixture into ice cream molds, popsicle molds, or a freezer-safe container.',
      'Freeze for at least 4 hours until firm.',
      'If using molds, remove from freezer and let sit at room temperature for 2-3 minutes before removing from molds.',
      'Store in an airtight container in the freezer for up to 2 weeks.',
      'Higher protein, lower sugar than regular ice cream.'
    ],
    notes: 'Frozen Greek yogurt with berries. Higher protein, lower sugar than regular ice cream.'
  },

  'avocado-chocolate-ice-cream': {
    title: 'Avocado Chocolate Ice Cream',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 170,
    ingredients: [
      { name: 'avocado', amount: 2, unit: 'piece', notes: 'large, ripe' },
      { name: 'dark chocolate', amount: 4, unit: 'oz', notes: '70% cacao or higher, melted' },
      { name: 'coconut milk', amount: 0.5, unit: 'cup', notes: 'full-fat' },
      { name: 'maple syrup', amount: 0.25, unit: 'cup', notes: 'or honey' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'cocoa powder', amount: 2, unit: 'tbsp', notes: 'unsweetened' },
      { name: 'salt', amount: 0.125, unit: 'tsp', notes: 'pinch' }
    ],
    steps: [
      'Cut avocados in half, remove pits, and scoop out flesh.',
      'Melt dark chocolate in a double boiler or microwave (stir every 30 seconds until smooth).',
      'In a food processor or blender, combine avocado flesh, melted chocolate, coconut milk, maple syrup, vanilla extract, cocoa powder, and salt.',
      'Blend until completely smooth and creamy (about 2-3 minutes).',
      'Pour mixture into a freezer-safe container.',
      'Freeze for at least 4 hours, stirring every hour for the first 2 hours to prevent ice crystals.',
      'Let sit at room temperature for 10 minutes before serving to soften.',
      'Store in an airtight container in the freezer for up to 2 weeks.',
      'Creamy ice cream made with avocado and dark chocolate. No dairy, healthy fats, lower sugar.'
    ],
    notes: 'Creamy ice cream made with avocado and dark chocolate. No dairy, healthy fats, lower sugar.'
  },

  'mango-sorbet': {
    title: 'Mango Sorbet',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 100,
    ingredients: [
      { name: 'mangoes', amount: 3, unit: 'piece', notes: 'ripe, peeled and chopped, frozen' },
      { name: 'lemon juice', amount: 1, unit: 'tbsp', notes: 'fresh' },
      { name: 'water', amount: 2, unit: 'tbsp', notes: 'if needed for blending' }
    ],
    steps: [
      'Peel and chop 3 ripe mangoes. Place in a freezer bag and freeze overnight (at least 8 hours).',
      'Remove frozen mango chunks from freezer and let sit at room temperature for 5 minutes to slightly soften.',
      'Place frozen mango chunks in a food processor or high-speed blender.',
      'Add fresh lemon juice.',
      'Blend on high speed, scraping down the sides as needed, until smooth and creamy (about 2-3 minutes).',
      'If the mixture is too thick, add 1-2 tbsp water and blend again.',
      'Serve immediately for soft-serve texture, or freeze for 30 minutes for a firmer texture.',
      'Store any leftovers in an airtight container in the freezer. Let sit at room temperature for 5 minutes before serving.',
      'Pureed frozen mango, naturally sweet. No dairy, no added sugar, high in Vitamin C.'
    ],
    notes: 'Pureed frozen mango, naturally sweet. No dairy, no added sugar, high in Vitamin C.'
  },

  'almond-milk-ice-cream': {
    title: 'Almond Milk Ice Cream',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 150,
    ingredients: [
      { name: 'almond milk', amount: 2, unit: 'cup', notes: 'unsweetened' },
      { name: 'dates', amount: 0.5, unit: 'cup', notes: 'pitted, medjool dates preferred' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'salt', amount: 0.125, unit: 'tsp', notes: 'pinch' },
      { name: 'maple syrup', amount: 1, unit: 'tbsp', notes: 'optional, for extra sweetness' }
    ],
    steps: [
      'Soak dates in hot water for 10 minutes to soften. Drain and reserve 2 tbsp of the soaking water.',
      'In a food processor or blender, blend soaked dates with reserved water until a smooth paste forms.',
      'Add almond milk, vanilla extract, salt, and maple syrup (if using). Blend until well combined.',
      'Pour mixture into a freezer-safe container.',
      'Freeze for at least 4 hours, stirring every hour for the first 2 hours to prevent ice crystals.',
      'Let sit at room temperature for 10 minutes before serving to soften.',
      'Store in an airtight container in the freezer for up to 2 weeks.',
      'Made with almond milk, lower in dairy. Can be sweetened with dates or minimal maple syrup.'
    ],
    notes: 'Made with almond milk, lower in dairy. Can be sweetened with dates or minimal maple syrup.'
  },

  'frozen-yogurt-with-berries': {
    title: 'Frozen Yogurt with Berries',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 145,
    ingredients: [
      { name: 'Greek yogurt', amount: 2, unit: 'cup', notes: 'plain, full-fat or low-fat' },
      { name: 'mixed berries', amount: 1.5, unit: 'cup', notes: 'fresh or frozen (strawberries, blueberries, raspberries)' },
      { name: 'maple syrup', amount: 2, unit: 'tbsp', notes: 'or honey, to taste' },
      { name: 'vanilla extract', amount: 0.5, unit: 'tsp', notes: 'optional' }
    ],
    steps: [
      'If using fresh berries, wash and dry them. If using frozen berries, let them thaw slightly.',
      'In a blender or food processor, blend 1 cup of berries until smooth. Reserve 0.5 cup whole berries for texture.',
      'In a large bowl, mix Greek yogurt, blended berries, whole berries, maple syrup, and vanilla extract (if using) until well combined.',
      'Pour mixture into a freezer-safe container.',
      'Freeze for at least 4 hours, stirring every hour for the first 2 hours to prevent ice crystals.',
      'Let sit at room temperature for 10 minutes before serving to soften.',
      'Store in an airtight container in the freezer for up to 2 weeks.',
      'Greek yogurt frozen with fresh berries. High protein, probiotics, lower sugar than regular ice cream.'
    ],
    notes: 'Greek yogurt frozen with fresh berries. High protein, probiotics, lower sugar than regular ice cream.'
  },

  'cashew-milk-ice-cream': {
    title: 'Cashew Milk Ice Cream',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 175,
    ingredients: [
      { name: 'raw cashews', amount: 1, unit: 'cup', notes: 'soaked overnight' },
      { name: 'water', amount: 1, unit: 'cup' },
      { name: 'maple syrup', amount: 0.25, unit: 'cup', notes: 'or honey' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'salt', amount: 0.125, unit: 'tsp', notes: 'pinch' }
    ],
    steps: [
      'Soak raw cashews in water overnight (at least 8 hours). Drain and rinse.',
      'In a high-speed blender, combine soaked cashews, 1 cup fresh water, maple syrup, vanilla extract, and salt.',
      'Blend on high speed until completely smooth and creamy (about 2-3 minutes).',
      'Pour mixture into a freezer-safe container.',
      'Freeze for at least 4 hours, stirring every hour for the first 2 hours to prevent ice crystals.',
      'Let sit at room temperature for 10 minutes before serving to soften.',
      'Store in an airtight container in the freezer for up to 2 weeks.',
      'Creamy ice cream made with cashew milk. Dairy-free, healthy fats, can be sweetened naturally.'
    ],
    notes: 'Creamy ice cream made with cashew milk. Dairy-free, healthy fats, can be sweetened naturally.'
  },

  // ============================================
  // MUFFIN RECIPES
  // ============================================
  'whole-wheat-blueberry-muffins': {
    title: 'Whole Wheat Blueberry Muffins',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 155,
    ingredients: [
      { name: 'whole wheat flour', amount: 2, unit: 'cup' },
      { name: 'blueberries', amount: 1, unit: 'cup', notes: 'fresh or frozen' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'coconut oil', amount: 0.33, unit: 'cup', notes: 'melted' },
      { name: 'maple syrup', amount: 0.25, unit: 'cup' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'baking soda', amount: 0.5, unit: 'tsp' },
      { name: 'cinnamon', amount: 0.5, unit: 'tsp', notes: 'ground' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 375°F (190°C). Line a 12-cup muffin tin with paper liners.',
      'In a large bowl, whisk together eggs, melted coconut oil, maple syrup, and vanilla extract.',
      'In a separate bowl, mix whole wheat flour, baking powder, baking soda, cinnamon, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Gently fold in blueberries (if using frozen, do not thaw).',
      'Spoon batter into prepared muffin cups, filling each about 2/3 full.',
      'Bake for 18-22 minutes until a toothpick inserted into the center comes out clean.',
      'Let cool in pan for 5 minutes, then transfer to a wire rack.',
      'Store in an airtight container at room temperature for up to 3 days.'
    ],
    notes: 'Made with whole wheat flour, fresh blueberries, and minimal added sugar. Higher fiber, lower glycemic than white flour muffins.'
  },

  'almond-flour-banana-muffins': {
    title: 'Almond Flour Banana Muffins',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 170,
    ingredients: [
      { name: 'almond flour', amount: 2, unit: 'cup' },
      { name: 'ripe bananas', amount: 2, unit: 'piece', notes: 'medium, very ripe (brown spots)' },
      { name: 'eggs', amount: 3, unit: 'piece', notes: 'large' },
      { name: 'coconut oil', amount: 0.25, unit: 'cup', notes: 'melted' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'cinnamon', amount: 0.5, unit: 'tsp', notes: 'ground' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Line a 12-cup muffin tin with paper liners.',
      'Mash ripe bananas in a large bowl until smooth.',
      'Add eggs, melted coconut oil, and vanilla extract. Mix well.',
      'In a separate bowl, mix almond flour, baking powder, cinnamon, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Spoon batter into prepared muffin cups, filling each about 2/3 full.',
      'Bake for 20-25 minutes until a toothpick inserted into the center comes out clean.',
      'Let cool in pan for 5 minutes, then transfer to a wire rack.',
      'Store in an airtight container at room temperature for up to 3 days.'
    ],
    notes: 'Made with almond flour, naturally sweetened with ripe bananas. Gluten-free, lower glycemic, high in healthy fats.'
  },

  'oat-flour-carrot-muffins': {
    title: 'Oat Flour Carrot Muffins',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 145,
    ingredients: [
      { name: 'oat flour', amount: 2, unit: 'cup', notes: 'or grind 2.5 cups rolled oats' },
      { name: 'carrots', amount: 1.5, unit: 'cup', notes: 'grated (about 2-3 medium carrots)' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'coconut oil', amount: 0.33, unit: 'cup', notes: 'melted' },
      { name: 'maple syrup', amount: 0.25, unit: 'cup' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'cinnamon', amount: 1, unit: 'tsp', notes: 'ground' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'baking soda', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 375°F (190°C). Line a 12-cup muffin tin with paper liners.',
      'If making oat flour: Grind 2.5 cups rolled oats in a food processor until fine.',
      'Grate carrots using a box grater.',
      'In a large bowl, whisk together eggs, melted coconut oil, maple syrup, and vanilla extract.',
      'In a separate bowl, mix oat flour, cinnamon, baking powder, baking soda, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Fold in grated carrots.',
      'Spoon batter into prepared muffin cups, filling each about 2/3 full.',
      'Bake for 18-22 minutes until a toothpick inserted into the center comes out clean.',
      'Let cool in pan for 5 minutes, then transfer to a wire rack.',
      'Store in an airtight container at room temperature for up to 3 days.'
    ],
    notes: 'Made with oat flour, grated carrots, and minimal sugar. High fiber, naturally sweet, anti-inflammatory.'
  },

  'zucchini-chocolate-chip-muffins': {
    title: 'Zucchini Chocolate Chip Muffins',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 120,
    ingredients: [
      { name: 'zucchini', amount: 1.5, unit: 'cup', notes: 'grated (about 1 large zucchini)' },
      { name: 'whole wheat flour', amount: 2, unit: 'cup' },
      { name: 'dark chocolate chips', amount: 0.5, unit: 'cup', notes: '70% cacao or higher' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'coconut oil', amount: 0.33, unit: 'cup', notes: 'melted' },
      { name: 'maple syrup', amount: 0.25, unit: 'cup' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'baking soda', amount: 0.5, unit: 'tsp' },
      { name: 'cinnamon', amount: 0.5, unit: 'tsp', notes: 'ground' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 375°F (190°C). Line a 12-cup muffin tin with paper liners.',
      'Grate zucchini using a box grater. Place in a clean kitchen towel and squeeze out excess moisture.',
      'In a large bowl, whisk together eggs, melted coconut oil, maple syrup, and vanilla extract.',
      'In a separate bowl, mix whole wheat flour, cinnamon, baking powder, baking soda, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Fold in grated zucchini and dark chocolate chips.',
      'Spoon batter into prepared muffin cups, filling each about 2/3 full.',
      'Bake for 18-22 minutes until a toothpick inserted into the center comes out clean.',
      'Let cool in pan for 5 minutes, then transfer to a wire rack.',
      'Store in an airtight container at room temperature for up to 3 days.'
    ],
    notes: 'Made with whole wheat flour, grated zucchini, and dark chocolate chips. Moist, high fiber, lower sugar.'
  },

  'pumpkin-spice-muffins': {
    title: 'Pumpkin Spice Muffins',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 150,
    ingredients: [
      { name: 'whole wheat flour', amount: 2, unit: 'cup' },
      { name: 'pumpkin puree', amount: 1, unit: 'can', notes: '15 oz, or 1.75 cups homemade' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'coconut oil', amount: 0.33, unit: 'cup', notes: 'melted' },
      { name: 'maple syrup', amount: 0.25, unit: 'cup' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'cinnamon', amount: 1, unit: 'tsp', notes: 'ground' },
      { name: 'ginger', amount: 0.5, unit: 'tsp', notes: 'ground' },
      { name: 'nutmeg', amount: 0.25, unit: 'tsp', notes: 'ground' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'baking soda', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 375°F (190°C). Line a 12-cup muffin tin with paper liners.',
      'In a large bowl, whisk together pumpkin puree, eggs, melted coconut oil, maple syrup, and vanilla extract.',
      'In a separate bowl, mix whole wheat flour, cinnamon, ginger, nutmeg, baking powder, baking soda, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Spoon batter into prepared muffin cups, filling each about 2/3 full.',
      'Bake for 18-22 minutes until a toothpick inserted into the center comes out clean.',
      'Let cool in pan for 5 minutes, then transfer to a wire rack.',
      'Store in an airtight container at room temperature for up to 3 days.'
    ],
    notes: 'Made with whole wheat flour, pumpkin puree, and spices. High in Vitamin A, lower sugar, anti-inflammatory.'
  },

  'apple-cinnamon-muffins': {
    title: 'Apple Cinnamon Muffins',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 140,
    ingredients: [
      { name: 'apples', amount: 2, unit: 'piece', notes: 'medium, peeled and grated' },
      { name: 'whole wheat flour', amount: 2, unit: 'cup' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'coconut oil', amount: 0.33, unit: 'cup', notes: 'melted' },
      { name: 'maple syrup', amount: 0.25, unit: 'cup' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'cinnamon', amount: 1.5, unit: 'tsp', notes: 'ground' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'baking soda', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 375°F (190°C). Line a 12-cup muffin tin with paper liners.',
      'Peel and grate apples using a box grater.',
      'In a large bowl, whisk together eggs, melted coconut oil, maple syrup, and vanilla extract.',
      'In a separate bowl, mix whole wheat flour, cinnamon, baking powder, baking soda, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Fold in grated apples.',
      'Spoon batter into prepared muffin cups, filling each about 2/3 full.',
      'Bake for 18-22 minutes until a toothpick inserted into the center comes out clean.',
      'Let cool in pan for 5 minutes, then transfer to a wire rack.',
      'Store in an airtight container at room temperature for up to 3 days.'
    ],
    notes: 'Made with whole wheat flour, grated apples, and cinnamon. Naturally sweet, high fiber, no refined sugar.'
  },

  'sweet-potato-muffins': {
    title: 'Sweet Potato Muffins',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 148,
    ingredients: [
      { name: 'sweet potato', amount: 1, unit: 'cup', notes: 'cooked and mashed' },
      { name: 'whole wheat flour', amount: 2, unit: 'cup' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'coconut oil', amount: 0.33, unit: 'cup', notes: 'melted' },
      { name: 'maple syrup', amount: 0.25, unit: 'cup' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'cinnamon', amount: 1, unit: 'tsp', notes: 'ground' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'baking soda', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 375°F (190°C). Line a 12-cup muffin tin with paper liners.',
      'Cook and mash sweet potato until smooth. Measure 1 cup.',
      'In a large bowl, whisk together mashed sweet potato, eggs, melted coconut oil, maple syrup, and vanilla extract.',
      'In a separate bowl, mix whole wheat flour, cinnamon, baking powder, baking soda, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Spoon batter into prepared muffin cups, filling each about 2/3 full.',
      'Bake for 18-22 minutes until a toothpick inserted into the center comes out clean.',
      'Let cool in pan for 5 minutes, then transfer to a wire rack.',
      'Store in an airtight container at room temperature for up to 3 days.'
    ],
    notes: 'Made with sweet potato puree, whole wheat flour, and minimal sugar. High in beta-carotene, lower glycemic.'
  },

  'date-and-walnut-muffins': {
    title: 'Date and Walnut Muffins',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 175,
    ingredients: [
      { name: 'dates', amount: 1, unit: 'cup', notes: 'pitted, medjool dates preferred' },
      { name: 'whole wheat flour', amount: 2, unit: 'cup' },
      { name: 'walnuts', amount: 0.5, unit: 'cup', notes: 'chopped' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'coconut oil', amount: 0.33, unit: 'cup', notes: 'melted' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'baking soda', amount: 0.5, unit: 'tsp' },
      { name: 'cinnamon', amount: 0.5, unit: 'tsp', notes: 'ground' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 375°F (190°C). Line a 12-cup muffin tin with paper liners.',
      'Soak dates in hot water for 10 minutes to soften. Drain and reserve 2 tbsp of the soaking water.',
      'In a food processor, blend soaked dates with reserved water until a smooth paste forms.',
      'In a large bowl, whisk together date paste, eggs, melted coconut oil, and vanilla extract.',
      'In a separate bowl, mix whole wheat flour, cinnamon, baking powder, baking soda, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Fold in chopped walnuts.',
      'Spoon batter into prepared muffin cups, filling each about 2/3 full.',
      'Bake for 18-22 minutes until a toothpick inserted into the center comes out clean.',
      'Let cool in pan for 5 minutes, then transfer to a wire rack.',
      'Store in an airtight container at room temperature for up to 3 days.'
    ],
    notes: 'Naturally sweetened with dates, made with whole wheat flour and walnuts. No refined sugar, high healthy fats.'
  },

  // ============================================
  // CROISSANT RECIPES
  // ============================================
  'whole-wheat-croissant-homemade': {
    title: 'Whole Wheat Croissant (Homemade)',
    cuisine: 'French',
    servings: 12,
    caloriesPerServing: 195,
    ingredients: [
      { name: 'whole wheat flour', amount: 3, unit: 'cup' },
      { name: 'active dry yeast', amount: 1, unit: 'tsp' },
      { name: 'warm water', amount: 0.5, unit: 'cup' },
      { name: 'butter', amount: 0.75, unit: 'cup', notes: 'cold, cut into small pieces (reduced from traditional 1.5 cups)' },
      { name: 'salt', amount: 1, unit: 'tsp' },
      { name: 'egg', amount: 1, unit: 'piece', notes: 'large, for egg wash' }
    ],
    steps: [
      'Dissolve yeast in warm water and let sit for 5 minutes until foamy.',
      'In a large bowl, mix whole wheat flour and salt.',
      'Add yeast mixture and mix until a dough forms. Knead for 5-7 minutes until smooth.',
      'Wrap dough in plastic wrap and refrigerate for 1 hour.',
      'Roll out dough into a rectangle. Place cold butter pieces in the center, fold dough over, and seal edges.',
      'Roll out into a rectangle again, then fold into thirds (like a letter). This is one "turn".',
      'Refrigerate for 30 minutes. Repeat the rolling and folding process 2 more times (3 turns total).',
      'After final turn, refrigerate for at least 2 hours or overnight.',
      'Preheat oven to 400°F (200°C). Line a baking sheet with parchment paper.',
      'Roll out dough to 1/4 inch thickness. Cut into triangles.',
      'Roll each triangle from the wide end to the point to form croissants.',
      'Place on prepared baking sheet. Brush with beaten egg.',
      'Bake for 15-20 minutes until golden brown.',
      'Let cool on a wire rack. Store in an airtight container at room temperature for up to 2 days.'
    ],
    notes: 'Made with whole wheat flour and minimal butter. Higher fiber, lower refined carbs than traditional croissants.'
  },

  'almond-flour-croissant': {
    title: 'Almond Flour Croissant',
    cuisine: 'French',
    servings: 12,
    caloriesPerServing: 185,
    ingredients: [
      { name: 'almond flour', amount: 2, unit: 'cup' },
      { name: 'coconut oil', amount: 0.5, unit: 'cup', notes: 'solid, cold' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' },
      { name: 'maple syrup', amount: 1, unit: 'tbsp', notes: 'optional, for slight sweetness' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Line a baking sheet with parchment paper.',
      'In a large bowl, mix almond flour, baking powder, and salt.',
      'Cut cold coconut oil into the flour mixture until it resembles coarse crumbs.',
      'In a separate bowl, whisk together eggs and maple syrup (if using).',
      'Add wet ingredients to dry ingredients and mix until a dough forms.',
      'Roll out dough on a floured surface to 1/4 inch thickness.',
      'Cut into triangles. Roll each triangle from the wide end to the point.',
      'Place on prepared baking sheet.',
      'Bake for 12-15 minutes until golden brown.',
      'Let cool on a wire rack. Store in an airtight container at room temperature for up to 2 days.'
    ],
    notes: 'Made with almond flour instead of white flour. Gluten-free, lower glycemic, higher healthy fats.'
  },

  'oat-flour-croissant': {
    title: 'Oat Flour Croissant',
    cuisine: 'French',
    servings: 12,
    caloriesPerServing: 190,
    ingredients: [
      { name: 'oat flour', amount: 2.5, unit: 'cup', notes: 'or grind 3 cups rolled oats' },
      { name: 'butter', amount: 0.5, unit: 'cup', notes: 'cold, cut into small pieces (reduced from traditional 1 cup)' },
      { name: 'active dry yeast', amount: 1, unit: 'tsp' },
      { name: 'warm water', amount: 0.5, unit: 'cup' },
      { name: 'salt', amount: 1, unit: 'tsp' },
      { name: 'egg', amount: 1, unit: 'piece', notes: 'large, for egg wash' }
    ],
    steps: [
      'If making oat flour: Grind 3 cups rolled oats in a food processor until fine.',
      'Dissolve yeast in warm water and let sit for 5 minutes until foamy.',
      'In a large bowl, mix oat flour and salt.',
      'Add yeast mixture and mix until a dough forms. Knead for 5-7 minutes until smooth.',
      'Wrap dough in plastic wrap and refrigerate for 1 hour.',
      'Roll out dough into a rectangle. Place cold butter pieces in the center, fold dough over, and seal edges.',
      'Roll out into a rectangle again, then fold into thirds (like a letter). This is one "turn".',
      'Refrigerate for 30 minutes. Repeat the rolling and folding process 2 more times (3 turns total).',
      'After final turn, refrigerate for at least 2 hours or overnight.',
      'Preheat oven to 400°F (200°C). Line a baking sheet with parchment paper.',
      'Roll out dough to 1/4 inch thickness. Cut into triangles.',
      'Roll each triangle from the wide end to the point to form croissants.',
      'Place on prepared baking sheet. Brush with beaten egg.',
      'Bake for 15-20 minutes until golden brown.',
      'Let cool on a wire rack. Store in an airtight container at room temperature for up to 2 days.'
    ],
    notes: 'Made with oat flour and minimal butter. Whole grain, higher fiber, lower glycemic than white flour croissants.'
  },

  'sweet-potato-croissant': {
    title: 'Sweet Potato Croissant',
    cuisine: 'French',
    servings: 12,
    caloriesPerServing: 180,
    ingredients: [
      { name: 'sweet potato', amount: 1, unit: 'cup', notes: 'cooked and mashed' },
      { name: 'whole wheat flour', amount: 2.5, unit: 'cup' },
      { name: 'butter', amount: 0.5, unit: 'cup', notes: 'cold, cut into small pieces (reduced from traditional 1 cup)' },
      { name: 'active dry yeast', amount: 1, unit: 'tsp' },
      { name: 'warm water', amount: 0.5, unit: 'cup' },
      { name: 'salt', amount: 1, unit: 'tsp' },
      { name: 'egg', amount: 1, unit: 'piece', notes: 'large, for egg wash' }
    ],
    steps: [
      'Cook and mash sweet potato until smooth. Measure 1 cup.',
      'Dissolve yeast in warm water and let sit for 5 minutes until foamy.',
      'In a large bowl, mix whole wheat flour, mashed sweet potato, and salt.',
      'Add yeast mixture and mix until a dough forms. Knead for 5-7 minutes until smooth.',
      'Wrap dough in plastic wrap and refrigerate for 1 hour.',
      'Roll out dough into a rectangle. Place cold butter pieces in the center, fold dough over, and seal edges.',
      'Roll out into a rectangle again, then fold into thirds (like a letter). This is one "turn".',
      'Refrigerate for 30 minutes. Repeat the rolling and folding process 2 more times (3 turns total).',
      'After final turn, refrigerate for at least 2 hours or overnight.',
      'Preheat oven to 400°F (200°C). Line a baking sheet with parchment paper.',
      'Roll out dough to 1/4 inch thickness. Cut into triangles.',
      'Roll each triangle from the wide end to the point to form croissants.',
      'Place on prepared baking sheet. Brush with beaten egg.',
      'Bake for 15-20 minutes until golden brown.',
      'Let cool on a wire rack. Store in an airtight container at room temperature for up to 2 days.'
    ],
    notes: 'Made with sweet potato puree and whole wheat flour. Naturally sweet, high in beta-carotene, lower butter.'
  },

  'spinach-and-feta-croissant-whole-wheat': {
    title: 'Spinach and Feta Croissant (Whole Wheat)',
    cuisine: 'French',
    servings: 12,
    caloriesPerServing: 205,
    ingredients: [
      { name: 'whole wheat flour', amount: 2.5, unit: 'cup' },
      { name: 'active dry yeast', amount: 1, unit: 'tsp' },
      { name: 'warm water', amount: 0.5, unit: 'cup' },
      { name: 'butter', amount: 0.5, unit: 'cup', notes: 'cold, cut into small pieces' },
      { name: 'spinach', amount: 2, unit: 'cup', notes: 'fresh, chopped' },
      { name: 'feta cheese', amount: 0.5, unit: 'cup', notes: 'crumbled, light/low-fat if available' },
      { name: 'garlic', amount: 1, unit: 'clove', notes: 'minced' },
      { name: 'salt', amount: 1, unit: 'tsp' },
      { name: 'egg', amount: 1, unit: 'piece', notes: 'large, for egg wash' }
    ],
    steps: [
      'Dissolve yeast in warm water and let sit for 5 minutes until foamy.',
      'In a large bowl, mix whole wheat flour and salt.',
      'Add yeast mixture and mix until a dough forms. Knead for 5-7 minutes until smooth.',
      'Wrap dough in plastic wrap and refrigerate for 1 hour.',
      'Sauté spinach with garlic until wilted. Let cool, then mix with crumbled feta cheese.',
      'Roll out dough into a rectangle. Place cold butter pieces in the center, fold dough over, and seal edges.',
      'Roll out into a rectangle again, then fold into thirds (like a letter). This is one "turn".',
      'Refrigerate for 30 minutes. Repeat the rolling and folding process 2 more times (3 turns total).',
      'After final turn, refrigerate for at least 2 hours or overnight.',
      'Preheat oven to 400°F (200°C). Line a baking sheet with parchment paper.',
      'Roll out dough to 1/4 inch thickness. Cut into triangles.',
      'Place a spoonful of spinach-feta mixture on each triangle.',
      'Roll each triangle from the wide end to the point to form croissants.',
      'Place on prepared baking sheet. Brush with beaten egg.',
      'Bake for 15-20 minutes until golden brown.',
      'Let cool on a wire rack. Store in an airtight container at room temperature for up to 2 days.'
    ],
    notes: 'Whole wheat croissant filled with spinach and light feta. Higher fiber, more nutrients, lower butter.'
  },

  // ============================================
  // CEREAL RECIPES
  // ============================================
  'steel-cut-oats-with-berries-and-nuts': {
    title: 'Steel-Cut Oats with Berries and Nuts',
    cuisine: 'American',
    servings: 2,
    caloriesPerServing: 250,
    ingredients: [
      { name: 'steel-cut oats', amount: 0.5, unit: 'cup' },
      { name: 'water', amount: 1.5, unit: 'cup' },
      { name: 'mixed berries', amount: 0.5, unit: 'cup', notes: 'fresh or frozen (strawberries, blueberries, raspberries)' },
      { name: 'nuts', amount: 0.25, unit: 'cup', notes: 'almonds, walnuts, or pecans, chopped' },
      { name: 'cinnamon', amount: 0.5, unit: 'tsp', notes: 'ground, optional' }
    ],
    steps: [
      'In a medium saucepan, bring water to a boil.',
      'Add steel-cut oats and reduce heat to low. Simmer for 20-25 minutes, stirring occasionally, until oats are tender and creamy.',
      'Remove from heat and let sit for 5 minutes.',
      'Top with mixed berries and chopped nuts.',
      'Sprinkle with cinnamon if desired.',
      'Serve immediately. Store any leftovers in the refrigerator for up to 3 days.'
    ],
    notes: 'Whole grain oats, high in fiber, topped with fresh berries and healthy fats from nuts. Lower glycemic than processed cereals.'
  },

  'quinoa-breakfast-bowl-with-fruit': {
    title: 'Quinoa Breakfast Bowl with Fruit',
    cuisine: 'American',
    servings: 2,
    caloriesPerServing: 270,
    ingredients: [
      { name: 'quinoa', amount: 0.5, unit: 'cup', notes: 'rinsed' },
      { name: 'water', amount: 1, unit: 'cup' },
      { name: 'almond milk', amount: 0.5, unit: 'cup', notes: 'or coconut milk' },
      { name: 'mixed fruit', amount: 0.5, unit: 'cup', notes: 'banana, berries, apple, etc.' },
      { name: 'cinnamon', amount: 0.5, unit: 'tsp', notes: 'ground, optional' }
    ],
    steps: [
      'In a medium saucepan, combine quinoa and water. Bring to a boil.',
      'Reduce heat to low, cover, and simmer for 15 minutes until quinoa is tender and water is absorbed.',
      'Stir in almond milk and continue cooking for 2-3 minutes until creamy.',
      'Remove from heat and let sit for 5 minutes.',
      'Top with mixed fruit and sprinkle with cinnamon if desired.',
      'Serve immediately. Store any leftovers in the refrigerator for up to 3 days.'
    ],
    notes: 'Protein-rich quinoa instead of processed grains, topped with fresh fruit. Higher protein, lower sugar.'
  },

  'homemade-granola-with-nuts-and-seeds': {
    title: 'Homemade Granola with Nuts and Seeds',
    cuisine: 'American',
    servings: 8,
    caloriesPerServing: 320,
    ingredients: [
      { name: 'rolled oats', amount: 3, unit: 'cup' },
      { name: 'nuts', amount: 1, unit: 'cup', notes: 'almonds, walnuts, pecans, chopped' },
      { name: 'seeds', amount: 0.5, unit: 'cup', notes: 'pumpkin seeds, sunflower seeds, chia seeds' },
      { name: 'coconut oil', amount: 0.25, unit: 'cup', notes: 'melted' },
      { name: 'maple syrup', amount: 0.25, unit: 'cup', notes: 'or honey' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'cinnamon', amount: 1, unit: 'tsp', notes: 'ground' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 325°F (165°C). Line a baking sheet with parchment paper.',
      'In a large bowl, mix rolled oats, nuts, and seeds.',
      'In a separate bowl, whisk together melted coconut oil, maple syrup, vanilla extract, cinnamon, and salt.',
      'Pour wet ingredients over dry ingredients and mix until evenly coated.',
      'Spread mixture in an even layer on the prepared baking sheet.',
      'Bake for 20-25 minutes, stirring halfway through, until golden brown.',
      'Let cool completely on the baking sheet (it will crisp as it cools).',
      'Store in an airtight container at room temperature for up to 2 weeks.',
      'Made with whole oats, nuts, seeds, and minimal added sugar. Higher fiber and healthy fats than store-bought.'
    ],
    notes: 'Made with whole oats, nuts, seeds, and minimal added sugar. Higher fiber and healthy fats than store-bought.'
  },

  'chia-seed-pudding-with-berries': {
    title: 'Chia Seed Pudding with Berries',
    cuisine: 'American',
    servings: 2,
    caloriesPerServing: 180,
    ingredients: [
      { name: 'chia seeds', amount: 0.25, unit: 'cup' },
      { name: 'almond milk', amount: 1, unit: 'cup', notes: 'or coconut milk' },
      { name: 'maple syrup', amount: 1, unit: 'tbsp', notes: 'or honey, optional' },
      { name: 'vanilla extract', amount: 0.5, unit: 'tsp' },
      { name: 'mixed berries', amount: 0.5, unit: 'cup', notes: 'fresh (strawberries, blueberries, raspberries)' }
    ],
    steps: [
      'In a medium bowl or jar, combine chia seeds, almond milk, maple syrup (if using), and vanilla extract.',
      'Stir well to combine. Let sit for 5 minutes, then stir again to prevent clumping.',
      'Cover and refrigerate for at least 4 hours or overnight until pudding thickens.',
      'Before serving, stir the pudding to break up any clumps.',
      'Top with fresh mixed berries.',
      'Serve immediately. Store in the refrigerator for up to 5 days.'
    ],
    notes: 'High-fiber chia seeds soaked in almond milk, topped with fresh berries. No processed grains, high omega-3.'
  },

  'buckwheat-porridge-with-fruit': {
    title: 'Buckwheat Porridge with Fruit',
    cuisine: 'American',
    servings: 2,
    caloriesPerServing: 240,
    ingredients: [
      { name: 'buckwheat groats', amount: 0.5, unit: 'cup', notes: 'raw' },
      { name: 'water', amount: 1.5, unit: 'cup' },
      { name: 'almond milk', amount: 0.5, unit: 'cup', notes: 'or coconut milk' },
      { name: 'mixed fruit', amount: 0.5, unit: 'cup', notes: 'banana, berries, apple, etc.' },
      { name: 'cinnamon', amount: 0.5, unit: 'tsp', notes: 'ground, optional' }
    ],
    steps: [
      'In a medium saucepan, combine buckwheat groats and water. Bring to a boil.',
      'Reduce heat to low, cover, and simmer for 15-20 minutes until buckwheat is tender and water is absorbed.',
      'Stir in almond milk and continue cooking for 2-3 minutes until creamy.',
      'Remove from heat and let sit for 5 minutes.',
      'Top with mixed fruit and sprinkle with cinnamon if desired.',
      'Serve immediately. Store any leftovers in the refrigerator for up to 3 days.'
    ],
    notes: 'Gluten-free buckwheat cooked like oatmeal, topped with fresh fruit. Lower glycemic, higher protein than regular cereal.'
  },

  'millet-porridge-with-nuts': {
    title: 'Millet Porridge with Nuts',
    cuisine: 'American',
    servings: 2,
    caloriesPerServing: 230,
    ingredients: [
      { name: 'millet', amount: 0.5, unit: 'cup', notes: 'rinsed' },
      { name: 'water', amount: 1.5, unit: 'cup' },
      { name: 'almond milk', amount: 0.5, unit: 'cup', notes: 'or coconut milk' },
      { name: 'nuts', amount: 0.25, unit: 'cup', notes: 'almonds, walnuts, or pecans, chopped' },
      { name: 'cinnamon', amount: 0.5, unit: 'tsp', notes: 'ground, optional' }
    ],
    steps: [
      'In a medium saucepan, combine millet and water. Bring to a boil.',
      'Reduce heat to low, cover, and simmer for 20-25 minutes until millet is tender and water is absorbed.',
      'Stir in almond milk and continue cooking for 2-3 minutes until creamy.',
      'Remove from heat and let sit for 5 minutes.',
      'Top with chopped nuts and sprinkle with cinnamon if desired.',
      'Serve immediately. Store any leftovers in the refrigerator for up to 3 days.'
    ],
    notes: 'Ancient grain millet cooked into a creamy porridge, topped with nuts. High fiber, lower sugar than processed cereals.'
  },

  'brown-rice-porridge-with-cinnamon': {
    title: 'Brown Rice Porridge with Cinnamon',
    cuisine: 'American',
    servings: 2,
    caloriesPerServing: 220,
    ingredients: [
      { name: 'brown rice', amount: 0.5, unit: 'cup', notes: 'short-grain preferred' },
      { name: 'water', amount: 2, unit: 'cup' },
      { name: 'almond milk', amount: 0.5, unit: 'cup', notes: 'or coconut milk' },
      { name: 'cinnamon', amount: 1, unit: 'tsp', notes: 'ground' },
      { name: 'maple syrup', amount: 1, unit: 'tbsp', notes: 'optional, for sweetness' }
    ],
    steps: [
      'In a medium saucepan, combine brown rice and water. Bring to a boil.',
      'Reduce heat to low, cover, and simmer for 40-45 minutes until rice is very tender and creamy.',
      'Stir in almond milk and cinnamon. Continue cooking for 5 minutes until creamy.',
      'Remove from heat and let sit for 5 minutes.',
      'Stir in maple syrup if desired.',
      'Serve immediately. Store any leftovers in the refrigerator for up to 3 days.'
    ],
    notes: 'Whole grain brown rice cooked into a warm porridge with cinnamon. Lower glycemic, more fiber than white rice cereal.'
  },

  'amaranth-porridge-with-fruit': {
    title: 'Amaranth Porridge with Fruit',
    cuisine: 'American',
    servings: 2,
    caloriesPerServing: 260,
    ingredients: [
      { name: 'amaranth', amount: 0.5, unit: 'cup', notes: 'rinsed' },
      { name: 'water', amount: 1.5, unit: 'cup' },
      { name: 'almond milk', amount: 0.5, unit: 'cup', notes: 'or coconut milk' },
      { name: 'mixed fruit', amount: 0.5, unit: 'cup', notes: 'banana, berries, apple, etc.' },
      { name: 'cinnamon', amount: 0.5, unit: 'tsp', notes: 'ground, optional' }
    ],
    steps: [
      'In a medium saucepan, combine amaranth and water. Bring to a boil.',
      'Reduce heat to low, cover, and simmer for 20-25 minutes until amaranth is tender and water is absorbed.',
      'Stir in almond milk and continue cooking for 2-3 minutes until creamy.',
      'Remove from heat and let sit for 5 minutes.',
      'Top with mixed fruit and sprinkle with cinnamon if desired.',
      'Serve immediately. Store any leftovers in the refrigerator for up to 3 days.'
    ],
    notes: 'Protein-rich amaranth cooked into porridge, topped with fresh fruit. Complete protein, high fiber, no processed grains.'
  },

  // ============================================
  // PASTA RECIPES
  // ============================================
  'whole-wheat-spaghetti-with-olive-oil-garlic-and-vegetables': {
    title: 'Whole-Wheat Spaghetti with Olive Oil, Garlic, and Vegetables',
    cuisine: 'Italian',
    servings: 4,
    caloriesPerServing: 360,
    ingredients: [
      { name: 'whole wheat spaghetti', amount: 12, unit: 'oz', notes: 'or 340g' },
      { name: 'olive oil', amount: 3, unit: 'tbsp' },
      { name: 'garlic', amount: 4, unit: 'clove', notes: 'minced' },
      { name: 'mixed vegetables', amount: 2, unit: 'cup', notes: 'bell peppers, zucchini, cherry tomatoes, spinach' },
      { name: 'red pepper flakes', amount: 0.5, unit: 'tsp', notes: 'optional, for heat' },
      { name: 'lemon juice', amount: 1, unit: 'tbsp', notes: 'fresh, optional' },
      { name: 'parmesan cheese', amount: 2, unit: 'tbsp', notes: 'grated, optional' },
      { name: 'salt', amount: 1, unit: 'tsp', notes: 'to taste' },
      { name: 'black pepper', amount: 0.5, unit: 'tsp', notes: 'to taste' }
    ],
    steps: [
      'Bring a large pot of salted water to a boil. Cook whole wheat spaghetti according to package directions until al dente.',
      'While pasta is cooking, heat 2 tbsp olive oil in a large pan over medium heat.',
      'Add minced garlic and cook for 1 minute until fragrant (do not brown).',
      'Add mixed vegetables and cook for 5-7 minutes until tender-crisp.',
      'Drain pasta, reserving 0.5 cup of pasta water.',
      'Add cooked pasta to the pan with vegetables. Toss with remaining 1 tbsp olive oil.',
      'Add red pepper flakes (if using) and lemon juice (if using). Toss to combine.',
      'If pasta seems dry, add a splash of reserved pasta water.',
      'Season with salt and black pepper to taste.',
      'Serve immediately, topped with parmesan cheese if desired.',
      'Complex carbs from whole grains, healthy fats from olive oil, and fiber from vegetables. No heavy cream or excessive cheese.'
    ],
    notes: 'Complex carbs from whole grains, healthy fats from olive oil, and fiber from vegetables. No heavy cream or excessive cheese.'
  },

  'zucchini-noodles-zoodles-with-marinara': {
    title: 'Zucchini Noodles (Zoodles) with Marinara',
    cuisine: 'Italian',
    servings: 4,
    caloriesPerServing: 180,
    ingredients: [
      { name: 'zucchini', amount: 4, unit: 'piece', notes: 'medium, spiralized' },
      { name: 'tomato sauce', amount: 1.5, unit: 'cup', notes: 'simple marinara' },
      { name: 'garlic', amount: 2, unit: 'clove', notes: 'minced' },
      { name: 'olive oil', amount: 1, unit: 'tbsp' },
      { name: 'mozzarella cheese', amount: 2, unit: 'oz', notes: 'light/low-fat, shredded, optional' },
      { name: 'fresh basil', amount: 2, unit: 'tbsp', notes: 'chopped' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' },
      { name: 'black pepper', amount: 0.25, unit: 'tsp', notes: 'to taste' }
    ],
    steps: [
      'Spiralize zucchini using a spiralizer or julienne peeler to create "zoodles".',
      'Heat olive oil in a large pan over medium heat.',
      'Add minced garlic and cook for 1 minute until fragrant.',
      'Add tomato sauce and bring to a simmer. Cook for 5 minutes.',
      'Add zucchini noodles and cook for 2-3 minutes until just tender (do not overcook - they should be slightly crisp).',
      'Remove from heat. Toss with fresh basil.',
      'If using, sprinkle with light mozzarella cheese.',
      'Season with salt and black pepper to taste.',
      'Serve immediately.',
      'Spiralized zucchini instead of pasta, topped with tomato sauce and light cheese. Lower in refined carbs.'
    ],
    notes: 'Spiralized zucchini instead of pasta, topped with tomato sauce and light cheese. Lower in refined carbs.'
  },

  'tomato-based-pasta-with-veggies-light-cheese': {
    title: 'Tomato-Based Pasta with Veggies & Light Cheese',
    cuisine: 'Italian',
    servings: 4,
    caloriesPerServing: 340,
    ingredients: [
      { name: 'whole wheat pasta', amount: 12, unit: 'oz', notes: 'penne, fusilli, or spaghetti' },
      { name: 'tomato sauce', amount: 2, unit: 'cup', notes: 'marinara or pomodoro sauce' },
      { name: 'mixed vegetables', amount: 2, unit: 'cup', notes: 'bell peppers, mushrooms, zucchini, spinach' },
      { name: 'garlic', amount: 3, unit: 'clove', notes: 'minced' },
      { name: 'olive oil', amount: 1, unit: 'tbsp' },
      { name: 'mozzarella cheese', amount: 2, unit: 'oz', notes: 'light/low-fat, shredded (minimal cheese)' },
      { name: 'fresh basil', amount: 2, unit: 'tbsp', notes: 'chopped' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' },
      { name: 'black pepper', amount: 0.25, unit: 'tsp', notes: 'to taste' }
    ],
    steps: [
      'Bring a large pot of salted water to a boil. Cook whole wheat pasta according to package directions until al dente.',
      'While pasta is cooking, heat olive oil in a large pan over medium heat.',
      'Add minced garlic and cook for 1 minute until fragrant.',
      'Add mixed vegetables and cook for 5-7 minutes until tender.',
      'Add tomato sauce and bring to a simmer. Cook for 5 minutes.',
      'Drain pasta, reserving 0.5 cup of pasta water.',
      'Add cooked pasta to the pan with sauce. Toss to combine.',
      'If pasta seems dry, add a splash of reserved pasta water.',
      'Remove from heat. Toss with fresh basil and sprinkle with minimal cheese (2 oz instead of 4-6 oz).',
      'Season with salt and black pepper to taste.',
      'Serve immediately.',
      'Marinara or pomodoro sauce instead of creamy alfredo. Add vegetables and use minimal cheese for flavor. Lower in dairy and saturated fat.'
    ],
    notes: 'Marinara or pomodoro sauce instead of creamy alfredo. Add vegetables and use minimal cheese for flavor. Lower in dairy and saturated fat.'
  },

  'spaghetti-squash-with-marinara': {
    title: 'Spaghetti Squash with Marinara',
    cuisine: 'Italian',
    servings: 4,
    caloriesPerServing: 200,
    ingredients: [
      { name: 'spaghetti squash', amount: 1, unit: 'piece', notes: 'medium (about 3-4 lbs)' },
      { name: 'tomato sauce', amount: 1.5, unit: 'cup', notes: 'simple marinara' },
      { name: 'garlic', amount: 2, unit: 'clove', notes: 'minced' },
      { name: 'olive oil', amount: 1, unit: 'tbsp' },
      { name: 'fresh basil', amount: 2, unit: 'tbsp', notes: 'chopped' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' },
      { name: 'black pepper', amount: 0.25, unit: 'tsp', notes: 'to taste' }
    ],
    steps: [
      'Preheat oven to 400°F (200°C).',
      'Cut spaghetti squash in half lengthwise. Scoop out seeds and stringy pulp.',
      'Place squash halves cut-side down on a baking sheet. Bake for 40-50 minutes until tender.',
      'Remove from oven and let cool for 10 minutes.',
      'Use a fork to scrape the squash flesh into "noodles".',
      'While squash is baking, heat olive oil in a pan over medium heat.',
      'Add minced garlic and cook for 1 minute until fragrant.',
      'Add tomato sauce and bring to a simmer. Cook for 5 minutes.',
      'Add spaghetti squash noodles to the pan. Toss with sauce.',
      'Remove from heat. Toss with fresh basil.',
      'Season with salt and black pepper to taste.',
      'Serve immediately.',
      'Spaghetti squash instead of pasta, topped with tomato sauce. Lower carbs, higher fiber, no refined grains.'
    ],
    notes: 'Spaghetti squash instead of pasta, topped with tomato sauce. Lower carbs, higher fiber, no refined grains.'
  },

  'lentil-pasta-with-vegetables': {
    title: 'Lentil Pasta with Vegetables',
    cuisine: 'Italian',
    servings: 4,
    caloriesPerServing: 390,
    ingredients: [
      { name: 'lentil pasta', amount: 12, unit: 'oz', notes: 'or 340g, store-bought or homemade' },
      { name: 'mixed vegetables', amount: 2, unit: 'cup', notes: 'bell peppers, zucchini, cherry tomatoes, spinach' },
      { name: 'garlic', amount: 3, unit: 'clove', notes: 'minced' },
      { name: 'olive oil', amount: 2, unit: 'tbsp' },
      { name: 'lemon juice', amount: 1, unit: 'tbsp', notes: 'fresh, optional' },
      { name: 'fresh basil', amount: 2, unit: 'tbsp', notes: 'chopped' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' },
      { name: 'black pepper', amount: 0.25, unit: 'tsp', notes: 'to taste' }
    ],
    steps: [
      'Bring a large pot of salted water to a boil. Cook lentil pasta according to package directions until al dente.',
      'While pasta is cooking, heat 1 tbsp olive oil in a large pan over medium heat.',
      'Add minced garlic and cook for 1 minute until fragrant.',
      'Add mixed vegetables and cook for 5-7 minutes until tender.',
      'Drain pasta, reserving 0.5 cup of pasta water.',
      'Add cooked pasta to the pan with vegetables. Toss with remaining 1 tbsp olive oil.',
      'Add lemon juice (if using) and fresh basil. Toss to combine.',
      'If pasta seems dry, add a splash of reserved pasta water.',
      'Season with salt and black pepper to taste.',
      'Serve immediately.',
      'Pasta made from lentils instead of wheat. Higher protein, higher fiber, lower glycemic impact.'
    ],
    notes: 'Pasta made from lentils instead of wheat. Higher protein, higher fiber, lower glycemic impact.'
  },

  'chickpea-pasta-with-olive-oil-and-vegetables': {
    title: 'Chickpea Pasta with Olive Oil and Vegetables',
    cuisine: 'Italian',
    servings: 4,
    caloriesPerServing: 385,
    ingredients: [
      { name: 'chickpea pasta', amount: 12, unit: 'oz', notes: 'or 340g, store-bought' },
      { name: 'mixed vegetables', amount: 2, unit: 'cup', notes: 'bell peppers, zucchini, cherry tomatoes, spinach' },
      { name: 'garlic', amount: 3, unit: 'clove', notes: 'minced' },
      { name: 'olive oil', amount: 3, unit: 'tbsp' },
      { name: 'lemon juice', amount: 1, unit: 'tbsp', notes: 'fresh' },
      { name: 'fresh basil', amount: 2, unit: 'tbsp', notes: 'chopped' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' },
      { name: 'black pepper', amount: 0.25, unit: 'tsp', notes: 'to taste' }
    ],
    steps: [
      'Bring a large pot of salted water to a boil. Cook chickpea pasta according to package directions until al dente.',
      'While pasta is cooking, heat 2 tbsp olive oil in a large pan over medium heat.',
      'Add minced garlic and cook for 1 minute until fragrant.',
      'Add mixed vegetables and cook for 5-7 minutes until tender.',
      'Drain pasta, reserving 0.5 cup of pasta water.',
      'Add cooked pasta to the pan with vegetables. Toss with remaining 1 tbsp olive oil and lemon juice.',
      'Add fresh basil. Toss to combine.',
      'If pasta seems dry, add a splash of reserved pasta water.',
      'Season with salt and black pepper to taste.',
      'Serve immediately.',
      'Pasta made from chickpeas, tossed with olive oil and vegetables. Higher protein, higher fiber, lower carbs.'
    ],
    notes: 'Pasta made from chickpeas, tossed with olive oil and vegetables. Higher protein, higher fiber, lower carbs.'
  },

  'brown-rice-pasta-with-vegetables': {
    title: 'Brown Rice Pasta with Vegetables',
    cuisine: 'Italian',
    servings: 4,
    caloriesPerServing: 375,
    ingredients: [
      { name: 'brown rice pasta', amount: 12, unit: 'oz', notes: 'or 340g, store-bought' },
      { name: 'mixed vegetables', amount: 2, unit: 'cup', notes: 'bell peppers, zucchini, cherry tomatoes, spinach' },
      { name: 'garlic', amount: 3, unit: 'clove', notes: 'minced' },
      { name: 'olive oil', amount: 2, unit: 'tbsp' },
      { name: 'lemon juice', amount: 1, unit: 'tbsp', notes: 'fresh, optional' },
      { name: 'fresh basil', amount: 2, unit: 'tbsp', notes: 'chopped' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' },
      { name: 'black pepper', amount: 0.25, unit: 'tsp', notes: 'to taste' }
    ],
    steps: [
      'Bring a large pot of salted water to a boil. Cook brown rice pasta according to package directions until al dente.',
      'While pasta is cooking, heat 1 tbsp olive oil in a large pan over medium heat.',
      'Add minced garlic and cook for 1 minute until fragrant.',
      'Add mixed vegetables and cook for 5-7 minutes until tender.',
      'Drain pasta, reserving 0.5 cup of pasta water.',
      'Add cooked pasta to the pan with vegetables. Toss with remaining 1 tbsp olive oil.',
      'Add lemon juice (if using) and fresh basil. Toss to combine.',
      'If pasta seems dry, add a splash of reserved pasta water.',
      'Season with salt and black pepper to taste.',
      'Serve immediately.',
      'Pasta made from brown rice, whole grain alternative. Higher fiber than white pasta, topped with vegetables.'
    ],
    notes: 'Pasta made from brown rice, whole grain alternative. Higher fiber than white pasta, topped with vegetables.'
  },

  'quinoa-pasta-with-vegetables': {
    title: 'Quinoa Pasta with Vegetables',
    cuisine: 'Italian',
    servings: 4,
    caloriesPerServing: 395,
    ingredients: [
      { name: 'quinoa pasta', amount: 12, unit: 'oz', notes: 'or 340g, store-bought' },
      { name: 'mixed vegetables', amount: 2, unit: 'cup', notes: 'bell peppers, zucchini, cherry tomatoes, spinach' },
      { name: 'garlic', amount: 3, unit: 'clove', notes: 'minced' },
      { name: 'olive oil', amount: 2, unit: 'tbsp' },
      { name: 'lemon juice', amount: 1, unit: 'tbsp', notes: 'fresh, optional' },
      { name: 'fresh basil', amount: 2, unit: 'tbsp', notes: 'chopped' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' },
      { name: 'black pepper', amount: 0.25, unit: 'tsp', notes: 'to taste' }
    ],
    steps: [
      'Bring a large pot of salted water to a boil. Cook quinoa pasta according to package directions until al dente.',
      'While pasta is cooking, heat 1 tbsp olive oil in a large pan over medium heat.',
      'Add minced garlic and cook for 1 minute until fragrant.',
      'Add mixed vegetables and cook for 5-7 minutes until tender.',
      'Drain pasta, reserving 0.5 cup of pasta water.',
      'Add cooked pasta to the pan with vegetables. Toss with remaining 1 tbsp olive oil.',
      'Add lemon juice (if using) and fresh basil. Toss to combine.',
      'If pasta seems dry, add a splash of reserved pasta water.',
      'Season with salt and black pepper to taste.',
      'Serve immediately.',
      'Pasta made from quinoa, complete protein source. Higher protein, higher fiber, lower glycemic than regular pasta.'
    ],
    notes: 'Pasta made from quinoa, complete protein source. Higher protein, higher fiber, lower glycemic than regular pasta.'
  },

  // ============================================
  // FRIES RECIPES
  // ============================================
  'oven-baked-potato-wedges': {
    title: 'Oven-Baked Potato Wedges',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 180,
    ingredients: [
      { name: 'potatoes', amount: 4, unit: 'piece', notes: 'medium, russet or Yukon gold' },
      { name: 'olive oil', amount: 2, unit: 'tbsp' },
      { name: 'paprika', amount: 1, unit: 'tsp' },
      { name: 'garlic powder', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' },
      { name: 'black pepper', amount: 0.25, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 425°F (220°C). Line a baking sheet with parchment paper.',
      'Scrub potatoes and cut into wedges (about 8 wedges per potato).',
      'In a large bowl, toss potato wedges with olive oil, paprika, garlic powder, salt, and black pepper until evenly coated.',
      'Spread wedges in a single layer on the prepared baking sheet.',
      'Bake for 25-30 minutes, flipping halfway through, until golden and crispy.',
      'Serve immediately.',
      'Baked with a little olive oil instead of deep frying to reduce oil load.'
    ],
    notes: 'Baked with a little olive oil instead of deep frying to reduce oil load.'
  },

  'sweet-potato-fries-baked': {
    title: 'Sweet Potato Fries (Baked)',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 150,
    ingredients: [
      { name: 'sweet potatoes', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'olive oil', amount: 2, unit: 'tbsp' },
      { name: 'paprika', amount: 0.5, unit: 'tsp' },
      { name: 'cinnamon', amount: 0.25, unit: 'tsp', notes: 'optional, for sweet version' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' },
      { name: 'black pepper', amount: 0.25, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 425°F (220°C). Line a baking sheet with parchment paper.',
      'Scrub sweet potatoes and cut into fry-shaped sticks (about 1/4 inch thick).',
      'In a large bowl, toss sweet potato fries with olive oil, paprika, cinnamon (if using), salt, and black pepper until evenly coated.',
      'Spread fries in a single layer on the prepared baking sheet.',
      'Bake for 20-25 minutes, flipping halfway through, until golden and crispy.',
      'Serve immediately.',
      'Baked sweet potato fries with minimal oil. Lower glycemic than white potatoes, more nutrients.'
    ],
    notes: 'Baked sweet potato fries with minimal oil. Lower glycemic than white potatoes, more nutrients.'
  },

  'carrot-fries-baked': {
    title: 'Carrot Fries (Baked)',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 90,
    ingredients: [
      { name: 'carrots', amount: 4, unit: 'piece', notes: 'large, peeled' },
      { name: 'olive oil', amount: 1, unit: 'tbsp' },
      { name: 'paprika', amount: 0.5, unit: 'tsp' },
      { name: 'garlic powder', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' },
      { name: 'black pepper', amount: 0.25, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 425°F (220°C). Line a baking sheet with parchment paper.',
      'Cut carrots into fry-shaped sticks (about 1/4 inch thick).',
      'In a large bowl, toss carrot fries with olive oil, paprika, garlic powder, salt, and black pepper until evenly coated.',
      'Spread fries in a single layer on the prepared baking sheet.',
      'Bake for 20-25 minutes, flipping halfway through, until golden and crispy.',
      'Serve immediately.',
      'Baked carrot sticks instead of potato fries. Lower carbs, higher fiber, high in beta-carotene.'
    ],
    notes: 'Baked carrot sticks instead of potato fries. Lower carbs, higher fiber, high in beta-carotene.'
  },

  'zucchini-fries-baked': {
    title: 'Zucchini Fries (Baked)',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 70,
    ingredients: [
      { name: 'zucchini', amount: 3, unit: 'piece', notes: 'medium' },
      { name: 'olive oil', amount: 1, unit: 'tbsp' },
      { name: 'garlic powder', amount: 0.5, unit: 'tsp' },
      { name: 'parmesan cheese', amount: 2, unit: 'tbsp', notes: 'grated, optional' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' },
      { name: 'black pepper', amount: 0.25, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 425°F (220°C). Line a baking sheet with parchment paper.',
      'Cut zucchini into fry-shaped sticks (about 1/4 inch thick).',
      'In a large bowl, toss zucchini fries with olive oil, garlic powder, salt, and black pepper until evenly coated.',
      'If using, sprinkle with parmesan cheese.',
      'Spread fries in a single layer on the prepared baking sheet.',
      'Bake for 15-20 minutes, flipping halfway through, until golden and crispy.',
      'Serve immediately.',
      'Baked zucchini sticks instead of potato fries. Lower carbs, higher fiber, lower glycemic.'
    ],
    notes: 'Baked zucchini sticks instead of potato fries. Lower carbs, higher fiber, lower glycemic.'
  },

  'air-fried-potato-wedges': {
    title: 'Air-Fried Potato Wedges',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 170,
    ingredients: [
      { name: 'potatoes', amount: 4, unit: 'piece', notes: 'medium, russet or Yukon gold' },
      { name: 'olive oil', amount: 1, unit: 'tbsp', notes: 'spray or brush' },
      { name: 'paprika', amount: 1, unit: 'tsp' },
      { name: 'garlic powder', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' },
      { name: 'black pepper', amount: 0.25, unit: 'tsp' }
    ],
    steps: [
      'Preheat air fryer to 400°F (200°C).',
      'Scrub potatoes and cut into wedges (about 8 wedges per potato).',
      'In a large bowl, toss potato wedges with olive oil, paprika, garlic powder, salt, and black pepper until evenly coated.',
      'Place wedges in the air fryer basket in a single layer (cook in batches if needed).',
      'Air fry for 15-20 minutes, shaking the basket halfway through, until golden and crispy.',
      'Serve immediately.',
      'Potato wedges cooked in an air fryer with minimal oil. Same crispy texture with 70-80% less oil.'
    ],
    notes: 'Potato wedges cooked in an air fryer with minimal oil. Same crispy texture with 70-80% less oil.'
  },

  'parsnip-fries-baked': {
    title: 'Parsnip Fries (Baked)',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 110,
    ingredients: [
      { name: 'parsnips', amount: 4, unit: 'piece', notes: 'large, peeled' },
      { name: 'olive oil', amount: 1, unit: 'tbsp' },
      { name: 'paprika', amount: 0.5, unit: 'tsp' },
      { name: 'garlic powder', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' },
      { name: 'black pepper', amount: 0.25, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 425°F (220°C). Line a baking sheet with parchment paper.',
      'Cut parsnips into fry-shaped sticks (about 1/4 inch thick).',
      'In a large bowl, toss parsnip fries with olive oil, paprika, garlic powder, salt, and black pepper until evenly coated.',
      'Spread fries in a single layer on the prepared baking sheet.',
      'Bake for 20-25 minutes, flipping halfway through, until golden and crispy.',
      'Serve immediately.',
      'Baked parsnip sticks instead of potato fries. Lower glycemic, higher fiber, more nutrients.'
    ],
    notes: 'Baked parsnip sticks instead of potato fries. Lower glycemic, higher fiber, more nutrients.'
  },

  'jicama-fries-baked': {
    title: 'Jicama Fries (Baked)',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 60,
    ingredients: [
      { name: 'jicama', amount: 1, unit: 'piece', notes: 'large, peeled' },
      { name: 'olive oil', amount: 1, unit: 'tbsp' },
      { name: 'paprika', amount: 0.5, unit: 'tsp' },
      { name: 'chili powder', amount: 0.5, unit: 'tsp' },
      { name: 'lime juice', amount: 1, unit: 'tbsp', notes: 'fresh' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' }
    ],
    steps: [
      'Preheat oven to 425°F (220°C). Line a baking sheet with parchment paper.',
      'Cut jicama into fry-shaped sticks (about 1/4 inch thick).',
      'In a large bowl, toss jicama fries with olive oil, paprika, chili powder, salt, and lime juice until evenly coated.',
      'Spread fries in a single layer on the prepared baking sheet.',
      'Bake for 20-25 minutes, flipping halfway through, until golden and crispy.',
      'Serve immediately.',
      'Baked jicama sticks instead of potato fries. Very low carbs, high fiber, crunchy texture.'
    ],
    notes: 'Baked jicama sticks instead of potato fries. Very low carbs, high fiber, crunchy texture.'
  },

  'turnip-fries-baked': {
    title: 'Turnip Fries (Baked)',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 100,
    ingredients: [
      { name: 'turnips', amount: 4, unit: 'piece', notes: 'medium, peeled' },
      { name: 'olive oil', amount: 1, unit: 'tbsp' },
      { name: 'paprika', amount: 0.5, unit: 'tsp' },
      { name: 'garlic powder', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp', notes: 'to taste' },
      { name: 'black pepper', amount: 0.25, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 425°F (220°C). Line a baking sheet with parchment paper.',
      'Cut turnips into fry-shaped sticks (about 1/4 inch thick).',
      'In a large bowl, toss turnip fries with olive oil, paprika, garlic powder, salt, and black pepper until evenly coated.',
      'Spread fries in a single layer on the prepared baking sheet.',
      'Bake for 20-25 minutes, flipping halfway through, until golden and crispy.',
      'Serve immediately.',
      'Baked turnip sticks instead of potato fries. Lower carbs, higher fiber, more nutrients than white potatoes.'
    ],
    notes: 'Baked turnip sticks instead of potato fries. Lower carbs, higher fiber, more nutrients than white potatoes.'
  },

  // ============================================
  // PANCAKE RECIPES
  // ============================================
  '3-ingredient-banana-oat-pancakes': {
    title: '3-Ingredient Banana Oat Pancakes',
    cuisine: 'American',
    servings: 8,
    caloriesPerServing: 195,
    ingredients: [
      { name: 'ripe bananas', amount: 2, unit: 'piece', notes: 'medium, very ripe (brown spots)' },
      { name: 'rolled oats', amount: 1, unit: 'cup', notes: 'old-fashioned or quick oats' },
      { name: 'milk', amount: 0.25, unit: 'cup', notes: 'almond milk, coconut milk, or regular milk' }
    ],
    steps: [
      'In a blender or food processor, blend rolled oats until they form a fine flour.',
      'Add ripe bananas and milk. Blend until smooth and creamy.',
      'Heat a non-stick pan or griddle over medium heat.',
      'Pour 1/4 cup of batter onto the pan for each pancake.',
      'Cook for 2-3 minutes until bubbles form on the surface, then flip.',
      'Cook for another 1-2 minutes until golden brown.',
      'Serve immediately. Top with fresh berries if desired.',
      'Mashed ripe bananas, oat flour, and milk cooked on a non-stick pan for a lower-sugar, higher-fiber pancake.'
    ],
    notes: 'Mashed ripe bananas, oat flour, and milk cooked on a non-stick pan for a lower-sugar, higher-fiber pancake.'
  },

  'whole-grain-pancakes-with-berries': {
    title: 'Whole Grain Pancakes with Berries',
    cuisine: 'American',
    servings: 8,
    caloriesPerServing: 195,
    ingredients: [
      { name: 'whole wheat flour', amount: 1.5, unit: 'cup' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'milk', amount: 1, unit: 'cup', notes: 'almond milk, coconut milk, or regular milk' },
      { name: 'coconut oil', amount: 2, unit: 'tbsp', notes: 'melted' },
      { name: 'maple syrup', amount: 1, unit: 'tbsp', notes: 'optional, for slight sweetness' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'baking soda', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' },
      { name: 'mixed berries', amount: 1, unit: 'cup', notes: 'fresh (strawberries, blueberries, raspberries) for topping' }
    ],
    steps: [
      'In a large bowl, whisk together eggs, milk, melted coconut oil, and maple syrup (if using).',
      'In a separate bowl, mix whole wheat flour, baking powder, baking soda, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined (do not overmix).',
      'Heat a non-stick pan or griddle over medium heat.',
      'Pour 1/4 cup of batter onto the pan for each pancake.',
      'Cook for 2-3 minutes until bubbles form on the surface, then flip.',
      'Cook for another 1-2 minutes until golden brown.',
      'Serve immediately, topped with fresh mixed berries instead of syrup.',
      'Use whole wheat or oat flour, minimal sugar, and top with fresh berries instead of syrup.'
    ],
    notes: 'Use whole wheat or oat flour, minimal sugar, and top with fresh berries instead of syrup.'
  },

  'almond-flour-pancakes': {
    title: 'Almond Flour Pancakes',
    cuisine: 'American',
    servings: 8,
    caloriesPerServing: 195,
    ingredients: [
      { name: 'almond flour', amount: 1.5, unit: 'cup' },
      { name: 'eggs', amount: 3, unit: 'piece', notes: 'large' },
      { name: 'milk', amount: 0.5, unit: 'cup', notes: 'almond milk or coconut milk' },
      { name: 'coconut oil', amount: 2, unit: 'tbsp', notes: 'melted' },
      { name: 'maple syrup', amount: 1, unit: 'tbsp', notes: 'optional' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'In a large bowl, whisk together eggs, milk, melted coconut oil, and maple syrup (if using).',
      'In a separate bowl, mix almond flour, baking powder, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Heat a non-stick pan or griddle over medium-low heat.',
      'Pour 1/4 cup of batter onto the pan for each pancake.',
      'Cook for 3-4 minutes until edges are set, then flip.',
      'Cook for another 2-3 minutes until golden brown.',
      'Serve immediately.',
      'Made with almond flour instead of white flour. Lower glycemic, higher protein, healthy fats.'
    ],
    notes: 'Made with almond flour instead of white flour. Lower glycemic, higher protein, healthy fats.'
  },

  'buckwheat-pancakes': {
    title: 'Buckwheat Pancakes',
    cuisine: 'American',
    servings: 8,
    caloriesPerServing: 195,
    ingredients: [
      { name: 'buckwheat flour', amount: 1.5, unit: 'cup' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'milk', amount: 1, unit: 'cup', notes: 'almond milk, coconut milk, or regular milk' },
      { name: 'coconut oil', amount: 2, unit: 'tbsp', notes: 'melted' },
      { name: 'maple syrup', amount: 1, unit: 'tbsp', notes: 'optional' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'baking soda', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'In a large bowl, whisk together eggs, milk, melted coconut oil, and maple syrup (if using).',
      'In a separate bowl, mix buckwheat flour, baking powder, baking soda, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Heat a non-stick pan or griddle over medium heat.',
      'Pour 1/4 cup of batter onto the pan for each pancake.',
      'Cook for 2-3 minutes until bubbles form on the surface, then flip.',
      'Cook for another 1-2 minutes until golden brown.',
      'Serve immediately.',
      'Made with buckwheat flour, naturally gluten-free. Lower glycemic, higher protein than white flour pancakes.'
    ],
    notes: 'Made with buckwheat flour, naturally gluten-free. Lower glycemic, higher protein than white flour pancakes.'
  },

  'sweet-potato-pancakes': {
    title: 'Sweet Potato Pancakes',
    cuisine: 'American',
    servings: 8,
    caloriesPerServing: 195,
    ingredients: [
      { name: 'sweet potato', amount: 1, unit: 'cup', notes: 'cooked and mashed' },
      { name: 'whole wheat flour', amount: 1, unit: 'cup' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'milk', amount: 0.75, unit: 'cup', notes: 'almond milk, coconut milk, or regular milk' },
      { name: 'coconut oil', amount: 2, unit: 'tbsp', notes: 'melted' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'cinnamon', amount: 0.5, unit: 'tsp', notes: 'ground' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Cook and mash sweet potato until smooth. Measure 1 cup.',
      'In a large bowl, whisk together mashed sweet potato, eggs, milk, and melted coconut oil.',
      'In a separate bowl, mix whole wheat flour, baking powder, cinnamon, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Heat a non-stick pan or griddle over medium heat.',
      'Pour 1/4 cup of batter onto the pan for each pancake.',
      'Cook for 2-3 minutes until bubbles form on the surface, then flip.',
      'Cook for another 1-2 minutes until golden brown.',
      'Serve immediately.',
      'Made with sweet potato puree and whole grain flour. Higher fiber, lower sugar, naturally sweet.'
    ],
    notes: 'Made with sweet potato puree and whole grain flour. Higher fiber, lower sugar, naturally sweet.'
  },

  'coconut-flour-pancakes': {
    title: 'Coconut Flour Pancakes',
    cuisine: 'American',
    servings: 8,
    caloriesPerServing: 195,
    ingredients: [
      { name: 'coconut flour', amount: 0.5, unit: 'cup' },
      { name: 'eggs', amount: 4, unit: 'piece', notes: 'large (coconut flour needs more eggs)' },
      { name: 'milk', amount: 0.75, unit: 'cup', notes: 'almond milk or coconut milk' },
      { name: 'coconut oil', amount: 2, unit: 'tbsp', notes: 'melted' },
      { name: 'maple syrup', amount: 1, unit: 'tbsp', notes: 'optional' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'In a large bowl, whisk together eggs, milk, melted coconut oil, and maple syrup (if using).',
      'In a separate bowl, mix coconut flour, baking powder, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined. Let sit for 5 minutes (coconut flour needs time to absorb liquid).',
      'Heat a non-stick pan or griddle over medium-low heat.',
      'Pour 1/4 cup of batter onto the pan for each pancake.',
      'Cook for 3-4 minutes until edges are set, then flip.',
      'Cook for another 2-3 minutes until golden brown.',
      'Serve immediately.',
      'Made with coconut flour, lower in carbs. Uses healthy fats from coconut, naturally gluten-free.'
    ],
    notes: 'Made with coconut flour, lower in carbs. Uses healthy fats from coconut, naturally gluten-free.'
  },

  'protein-pancakes-with-greek-yogurt': {
    title: 'Protein Pancakes with Greek Yogurt',
    cuisine: 'American',
    servings: 8,
    caloriesPerServing: 195,
    ingredients: [
      { name: 'whole wheat flour', amount: 1, unit: 'cup' },
      { name: 'Greek yogurt', amount: 0.5, unit: 'cup', notes: 'plain, full-fat or low-fat' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'milk', amount: 0.5, unit: 'cup', notes: 'almond milk, coconut milk, or regular milk' },
      { name: 'coconut oil', amount: 1, unit: 'tbsp', notes: 'melted' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'baking soda', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'In a large bowl, whisk together Greek yogurt, eggs, milk, and melted coconut oil.',
      'In a separate bowl, mix whole wheat flour, baking powder, baking soda, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Heat a non-stick pan or griddle over medium heat.',
      'Pour 1/4 cup of batter onto the pan for each pancake.',
      'Cook for 2-3 minutes until bubbles form on the surface, then flip.',
      'Cook for another 1-2 minutes until golden brown.',
      'Serve immediately.',
      'Made with protein powder or Greek yogurt, whole grain flour. Higher protein, lower sugar.'
    ],
    notes: 'Made with protein powder or Greek yogurt, whole grain flour. Higher protein, lower sugar.'
  },

  'chia-seed-pancakes': {
    title: 'Chia Seed Pancakes',
    cuisine: 'American',
    servings: 8,
    caloriesPerServing: 195,
    ingredients: [
      { name: 'whole wheat flour', amount: 1, unit: 'cup' },
      { name: 'chia seeds', amount: 0.25, unit: 'cup' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'milk', amount: 1, unit: 'cup', notes: 'almond milk, coconut milk, or regular milk' },
      { name: 'coconut oil', amount: 2, unit: 'tbsp', notes: 'melted' },
      { name: 'maple syrup', amount: 1, unit: 'tbsp', notes: 'optional' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'In a large bowl, whisk together eggs, milk, melted coconut oil, and maple syrup (if using).',
      'In a separate bowl, mix whole wheat flour, chia seeds, baking powder, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined. Let sit for 5 minutes (chia seeds will absorb liquid).',
      'Heat a non-stick pan or griddle over medium heat.',
      'Pour 1/4 cup of batter onto the pan for each pancake.',
      'Cook for 2-3 minutes until bubbles form on the surface, then flip.',
      'Cook for another 1-2 minutes until golden brown.',
      'Serve immediately.',
      'Made with chia seeds and whole grain flour. High fiber, omega-3s, lower glycemic.'
    ],
    notes: 'Made with chia seeds and whole grain flour. High fiber, omega-3s, lower glycemic.'
  },

  // ============================================
  // WAFFLE RECIPES
  // ============================================
  'whole-grain-waffles-with-berries': {
    title: 'Whole Grain Waffles with Berries',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 230,
    ingredients: [
      { name: 'whole wheat flour', amount: 1.5, unit: 'cup' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'milk', amount: 1.25, unit: 'cup', notes: 'almond milk, coconut milk, or regular milk' },
      { name: 'coconut oil', amount: 3, unit: 'tbsp', notes: 'melted' },
      { name: 'maple syrup', amount: 1, unit: 'tbsp', notes: 'optional, for slight sweetness' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'baking soda', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' },
      { name: 'mixed berries', amount: 1, unit: 'cup', notes: 'fresh (strawberries, blueberries, raspberries) for topping' }
    ],
    steps: [
      'Preheat waffle iron according to manufacturer instructions.',
      'In a large bowl, whisk together eggs, milk, melted coconut oil, and maple syrup (if using).',
      'In a separate bowl, mix whole wheat flour, baking powder, baking soda, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined (do not overmix).',
      'Pour batter onto the preheated waffle iron (amount depends on your waffle iron size).',
      'Cook according to waffle iron instructions until golden and crispy.',
      'Serve immediately, topped with fresh mixed berries instead of syrup.',
      'Made with whole wheat or oat flour, minimal sugar, topped with fresh berries instead of syrup.'
    ],
    notes: 'Made with whole wheat or oat flour, minimal sugar, topped with fresh berries instead of syrup.'
  },

  'almond-flour-waffles': {
    title: 'Almond Flour Waffles',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 250,
    ingredients: [
      { name: 'almond flour', amount: 1.5, unit: 'cup' },
      { name: 'eggs', amount: 3, unit: 'piece', notes: 'large' },
      { name: 'milk', amount: 0.75, unit: 'cup', notes: 'almond milk or coconut milk' },
      { name: 'coconut oil', amount: 3, unit: 'tbsp', notes: 'melted' },
      { name: 'maple syrup', amount: 1, unit: 'tbsp', notes: 'optional' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat waffle iron according to manufacturer instructions.',
      'In a large bowl, whisk together eggs, milk, melted coconut oil, and maple syrup (if using).',
      'In a separate bowl, mix almond flour, baking powder, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Pour batter onto the preheated waffle iron.',
      'Cook according to waffle iron instructions until golden and crispy.',
      'Serve immediately.',
      'Made with almond flour instead of white flour. Lower glycemic, higher protein, healthy fats.'
    ],
    notes: 'Made with almond flour instead of white flour. Lower glycemic, higher protein, healthy fats.'
  },

  'buckwheat-waffles': {
    title: 'Buckwheat Waffles',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 220,
    ingredients: [
      { name: 'buckwheat flour', amount: 1.5, unit: 'cup' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'milk', amount: 1.25, unit: 'cup', notes: 'almond milk, coconut milk, or regular milk' },
      { name: 'coconut oil', amount: 3, unit: 'tbsp', notes: 'melted' },
      { name: 'maple syrup', amount: 1, unit: 'tbsp', notes: 'optional' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'baking soda', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat waffle iron according to manufacturer instructions.',
      'In a large bowl, whisk together eggs, milk, melted coconut oil, and maple syrup (if using).',
      'In a separate bowl, mix buckwheat flour, baking powder, baking soda, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Pour batter onto the preheated waffle iron.',
      'Cook according to waffle iron instructions until golden and crispy.',
      'Serve immediately.',
      'Made with buckwheat flour, naturally gluten-free. Lower glycemic, higher protein.'
    ],
    notes: 'Made with buckwheat flour, naturally gluten-free. Lower glycemic, higher protein.'
  },

  'sweet-potato-waffles': {
    title: 'Sweet Potato Waffles',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 210,
    ingredients: [
      { name: 'sweet potato', amount: 1, unit: 'cup', notes: 'cooked and mashed' },
      { name: 'whole wheat flour', amount: 1, unit: 'cup' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'milk', amount: 1, unit: 'cup', notes: 'almond milk, coconut milk, or regular milk' },
      { name: 'coconut oil', amount: 3, unit: 'tbsp', notes: 'melted' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'cinnamon', amount: 0.5, unit: 'tsp', notes: 'ground' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat waffle iron according to manufacturer instructions.',
      'Cook and mash sweet potato until smooth. Measure 1 cup.',
      'In a large bowl, whisk together mashed sweet potato, eggs, milk, and melted coconut oil.',
      'In a separate bowl, mix whole wheat flour, baking powder, cinnamon, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Pour batter onto the preheated waffle iron.',
      'Cook according to waffle iron instructions until golden and crispy.',
      'Serve immediately.',
      'Made with sweet potato puree and whole grain flour. Higher fiber, lower sugar, naturally sweet.'
    ],
    notes: 'Made with sweet potato puree and whole grain flour. Higher fiber, lower sugar, naturally sweet.'
  },

  'protein-waffles-with-greek-yogurt': {
    title: 'Protein Waffles with Greek Yogurt',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 260,
    ingredients: [
      { name: 'whole wheat flour', amount: 1, unit: 'cup' },
      { name: 'Greek yogurt', amount: 0.5, unit: 'cup', notes: 'plain, full-fat or low-fat' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'milk', amount: 0.75, unit: 'cup', notes: 'almond milk, coconut milk, or regular milk' },
      { name: 'coconut oil', amount: 2, unit: 'tbsp', notes: 'melted' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'baking soda', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat waffle iron according to manufacturer instructions.',
      'In a large bowl, whisk together Greek yogurt, eggs, milk, and melted coconut oil.',
      'In a separate bowl, mix whole wheat flour, baking powder, baking soda, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Pour batter onto the preheated waffle iron.',
      'Cook according to waffle iron instructions until golden and crispy.',
      'Serve immediately.',
      'Made with protein powder or Greek yogurt, whole grain flour. Higher protein, lower sugar.'
    ],
    notes: 'Made with protein powder or Greek yogurt, whole grain flour. Higher protein, lower sugar.'
  },

  'coconut-flour-waffles': {
    title: 'Coconut Flour Waffles',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 245,
    ingredients: [
      { name: 'coconut flour', amount: 0.5, unit: 'cup' },
      { name: 'eggs', amount: 4, unit: 'piece', notes: 'large (coconut flour needs more eggs)' },
      { name: 'milk', amount: 1, unit: 'cup', notes: 'almond milk or coconut milk' },
      { name: 'coconut oil', amount: 3, unit: 'tbsp', notes: 'melted' },
      { name: 'maple syrup', amount: 1, unit: 'tbsp', notes: 'optional' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat waffle iron according to manufacturer instructions.',
      'In a large bowl, whisk together eggs, milk, melted coconut oil, and maple syrup (if using).',
      'In a separate bowl, mix coconut flour, baking powder, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined. Let sit for 5 minutes (coconut flour needs time to absorb liquid).',
      'Pour batter onto the preheated waffle iron.',
      'Cook according to waffle iron instructions until golden and crispy.',
      'Serve immediately.',
      'Made with coconut flour, lower in carbs. Uses healthy fats from coconut, naturally gluten-free.'
    ],
    notes: 'Made with coconut flour, lower in carbs. Uses healthy fats from coconut, naturally gluten-free.'
  },

  'oat-flour-waffles': {
    title: 'Oat Flour Waffles',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 235,
    ingredients: [
      { name: 'oat flour', amount: 1.5, unit: 'cup', notes: 'or grind 2 cups rolled oats' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'milk', amount: 1.25, unit: 'cup', notes: 'almond milk, coconut milk, or regular milk' },
      { name: 'coconut oil', amount: 3, unit: 'tbsp', notes: 'melted' },
      { name: 'maple syrup', amount: 1, unit: 'tbsp', notes: 'optional' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'baking soda', amount: 0.5, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat waffle iron according to manufacturer instructions.',
      'If making oat flour: Grind 2 cups rolled oats in a food processor until fine.',
      'In a large bowl, whisk together eggs, milk, melted coconut oil, and maple syrup (if using).',
      'In a separate bowl, mix oat flour, baking powder, baking soda, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Pour batter onto the preheated waffle iron.',
      'Cook according to waffle iron instructions until golden and crispy.',
      'Serve immediately.',
      'Made with oat flour, whole grain alternative. Higher fiber, lower glycemic than white flour.'
    ],
    notes: 'Made with oat flour, whole grain alternative. Higher fiber, lower glycemic than white flour.'
  },

  'chia-seed-waffles': {
    title: 'Chia Seed Waffles',
    cuisine: 'American',
    servings: 4,
    caloriesPerServing: 240,
    ingredients: [
      { name: 'whole wheat flour', amount: 1, unit: 'cup' },
      { name: 'chia seeds', amount: 0.25, unit: 'cup' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'milk', amount: 1.25, unit: 'cup', notes: 'almond milk, coconut milk, or regular milk' },
      { name: 'coconut oil', amount: 3, unit: 'tbsp', notes: 'melted' },
      { name: 'maple syrup', amount: 1, unit: 'tbsp', notes: 'optional' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat waffle iron according to manufacturer instructions.',
      'In a large bowl, whisk together eggs, milk, melted coconut oil, and maple syrup (if using).',
      'In a separate bowl, mix whole wheat flour, chia seeds, baking powder, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined. Let sit for 5 minutes (chia seeds will absorb liquid).',
      'Pour batter onto the preheated waffle iron.',
      'Cook according to waffle iron instructions until golden and crispy.',
      'Serve immediately.',
      'Made with chia seeds and whole grain flour. High fiber, omega-3s, lower glycemic.'
    ],
    notes: 'Made with chia seeds and whole grain flour. High fiber, omega-3s, lower glycemic.'
  },

  // ============================================
  // DONUT RECIPES
  // ============================================
  'baked-donut-holes-whole-grain': {
    title: 'Baked Donut Holes (Whole Grain)',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 115,
    ingredients: [
      { name: 'whole wheat flour', amount: 1.5, unit: 'cup' },
      { name: 'eggs', amount: 1, unit: 'piece', notes: 'large' },
      { name: 'milk', amount: 0.5, unit: 'cup', notes: 'almond milk, coconut milk, or regular milk' },
      { name: 'coconut oil', amount: 2, unit: 'tbsp', notes: 'melted' },
      { name: 'maple syrup', amount: 0.25, unit: 'cup' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'cinnamon', amount: 0.5, unit: 'tsp', notes: 'ground' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Grease a mini muffin tin or donut hole pan.',
      'In a large bowl, whisk together egg, milk, melted coconut oil, maple syrup, and vanilla extract.',
      'In a separate bowl, mix whole wheat flour, baking powder, cinnamon, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Spoon batter into prepared pan, filling each cavity about 2/3 full.',
      'Bake for 10-12 minutes until golden and a toothpick inserted comes out clean.',
      'Let cool in pan for 5 minutes, then transfer to a wire rack.',
      'Store in an airtight container at room temperature for up to 3 days.',
      'Made with whole grain flour, baked instead of fried. Lower oil, higher fiber, minimal sugar.'
    ],
    notes: 'Made with whole grain flour, baked instead of fried. Lower oil, higher fiber, minimal sugar.'
  },

  'almond-flour-donuts-baked': {
    title: 'Almond Flour Donuts (Baked)',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 130,
    ingredients: [
      { name: 'almond flour', amount: 1.5, unit: 'cup' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'milk', amount: 0.5, unit: 'cup', notes: 'almond milk or coconut milk' },
      { name: 'coconut oil', amount: 2, unit: 'tbsp', notes: 'melted' },
      { name: 'maple syrup', amount: 0.25, unit: 'cup' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Grease a donut pan or mini muffin tin.',
      'In a large bowl, whisk together eggs, milk, melted coconut oil, maple syrup, and vanilla extract.',
      'In a separate bowl, mix almond flour, baking powder, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Spoon batter into prepared pan.',
      'Bake for 12-15 minutes until golden and a toothpick inserted comes out clean.',
      'Let cool in pan for 5 minutes, then transfer to a wire rack.',
      'Store in an airtight container at room temperature for up to 3 days.',
      'Made with almond flour, baked instead of fried. Lower glycemic, healthy fats, no refined flour.'
    ],
    notes: 'Made with almond flour, baked instead of fried. Lower glycemic, healthy fats, no refined flour.'
  },

  'sweet-potato-donut-holes-baked': {
    title: 'Sweet Potato Donut Holes (Baked)',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 110,
    ingredients: [
      { name: 'sweet potato', amount: 0.75, unit: 'cup', notes: 'cooked and mashed' },
      { name: 'whole wheat flour', amount: 1, unit: 'cup' },
      { name: 'eggs', amount: 1, unit: 'piece', notes: 'large' },
      { name: 'milk', amount: 0.5, unit: 'cup', notes: 'almond milk, coconut milk, or regular milk' },
      { name: 'coconut oil', amount: 2, unit: 'tbsp', notes: 'melted' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'cinnamon', amount: 0.5, unit: 'tsp', notes: 'ground' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Grease a mini muffin tin or donut hole pan.',
      'Cook and mash sweet potato until smooth. Measure 0.75 cup.',
      'In a large bowl, whisk together mashed sweet potato, egg, milk, and melted coconut oil.',
      'In a separate bowl, mix whole wheat flour, baking powder, cinnamon, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Spoon batter into prepared pan, filling each cavity about 2/3 full.',
      'Bake for 10-12 minutes until golden and a toothpick inserted comes out clean.',
      'Let cool in pan for 5 minutes, then transfer to a wire rack.',
      'Store in an airtight container at room temperature for up to 3 days.',
      'Made with sweet potato puree and whole grain flour, baked. Higher fiber, lower sugar, naturally sweet.'
    ],
    notes: 'Made with sweet potato puree and whole grain flour, baked. Higher fiber, lower sugar, naturally sweet.'
  },

  'protein-donut-holes-baked': {
    title: 'Protein Donut Holes (Baked)',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 120,
    ingredients: [
      { name: 'whole wheat flour', amount: 1, unit: 'cup' },
      { name: 'Greek yogurt', amount: 0.5, unit: 'cup', notes: 'plain, full-fat or low-fat' },
      { name: 'eggs', amount: 1, unit: 'piece', notes: 'large' },
      { name: 'milk', amount: 0.5, unit: 'cup', notes: 'almond milk, coconut milk, or regular milk' },
      { name: 'coconut oil', amount: 2, unit: 'tbsp', notes: 'melted' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'cinnamon', amount: 0.5, unit: 'tsp', notes: 'ground' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Grease a mini muffin tin or donut hole pan.',
      'In a large bowl, whisk together Greek yogurt, egg, milk, and melted coconut oil.',
      'In a separate bowl, mix whole wheat flour, baking powder, cinnamon, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Spoon batter into prepared pan, filling each cavity about 2/3 full.',
      'Bake for 10-12 minutes until golden and a toothpick inserted comes out clean.',
      'Let cool in pan for 5 minutes, then transfer to a wire rack.',
      'Store in an airtight container at room temperature for up to 3 days.',
      'Made with protein powder or Greek yogurt, whole grain flour, baked. Higher protein, lower sugar, no frying.'
    ],
    notes: 'Made with protein powder or Greek yogurt, whole grain flour, baked. Higher protein, lower sugar, no frying.'
  },

  'oat-flour-donuts-baked': {
    title: 'Oat Flour Donuts (Baked)',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 125,
    ingredients: [
      { name: 'oat flour', amount: 1.5, unit: 'cup', notes: 'or grind 2 cups rolled oats' },
      { name: 'eggs', amount: 1, unit: 'piece', notes: 'large' },
      { name: 'milk', amount: 0.5, unit: 'cup', notes: 'almond milk, coconut milk, or regular milk' },
      { name: 'coconut oil', amount: 2, unit: 'tbsp', notes: 'melted' },
      { name: 'maple syrup', amount: 0.25, unit: 'cup' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'cinnamon', amount: 0.5, unit: 'tsp', notes: 'ground' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Grease a donut pan or mini muffin tin.',
      'If making oat flour: Grind 2 cups rolled oats in a food processor until fine.',
      'In a large bowl, whisk together egg, milk, melted coconut oil, maple syrup, and vanilla extract.',
      'In a separate bowl, mix oat flour, baking powder, cinnamon, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Spoon batter into prepared pan.',
      'Bake for 12-15 minutes until golden and a toothpick inserted comes out clean.',
      'Let cool in pan for 5 minutes, then transfer to a wire rack.',
      'Store in an airtight container at room temperature for up to 3 days.',
      'Made with oat flour, baked instead of fried. Higher fiber, lower glycemic, minimal oil.'
    ],
    notes: 'Made with oat flour, baked instead of fried. Higher fiber, lower glycemic, minimal oil.'
  },

  'coconut-flour-donuts-baked': {
    title: 'Coconut Flour Donuts (Baked)',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 135,
    ingredients: [
      { name: 'coconut flour', amount: 0.5, unit: 'cup' },
      { name: 'eggs', amount: 3, unit: 'piece', notes: 'large (coconut flour needs more eggs)' },
      { name: 'milk', amount: 0.5, unit: 'cup', notes: 'almond milk or coconut milk' },
      { name: 'coconut oil', amount: 2, unit: 'tbsp', notes: 'melted' },
      { name: 'maple syrup', amount: 0.25, unit: 'cup' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Grease a donut pan or mini muffin tin.',
      'In a large bowl, whisk together eggs, milk, melted coconut oil, maple syrup, and vanilla extract.',
      'In a separate bowl, mix coconut flour, baking powder, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined. Let sit for 5 minutes (coconut flour needs time to absorb liquid).',
      'Spoon batter into prepared pan.',
      'Bake for 12-15 minutes until golden and a toothpick inserted comes out clean.',
      'Let cool in pan for 5 minutes, then transfer to a wire rack.',
      'Store in an airtight container at room temperature for up to 3 days.',
      'Made with coconut flour, baked. Lower carbs, healthy fats, naturally gluten-free.'
    ],
    notes: 'Made with coconut flour, baked. Lower carbs, healthy fats, naturally gluten-free.'
  },

  'date-sweetened-donut-holes-baked': {
    title: 'Date-Sweetened Donut Holes (Baked)',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 105,
    ingredients: [
      { name: 'dates', amount: 0.75, unit: 'cup', notes: 'pitted, medjool dates preferred' },
      { name: 'whole wheat flour', amount: 1.5, unit: 'cup' },
      { name: 'eggs', amount: 1, unit: 'piece', notes: 'large' },
      { name: 'milk', amount: 0.5, unit: 'cup', notes: 'almond milk, coconut milk, or regular milk' },
      { name: 'coconut oil', amount: 2, unit: 'tbsp', notes: 'melted' },
      { name: 'vanilla extract', amount: 1, unit: 'tsp' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'cinnamon', amount: 0.5, unit: 'tsp', notes: 'ground' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Grease a mini muffin tin or donut hole pan.',
      'Soak dates in hot water for 10 minutes to soften. Drain and reserve 2 tbsp of the soaking water.',
      'In a food processor, blend soaked dates with reserved water until a smooth paste forms.',
      'In a large bowl, whisk together date paste, egg, milk, melted coconut oil, and vanilla extract.',
      'In a separate bowl, mix whole wheat flour, baking powder, cinnamon, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Spoon batter into prepared pan, filling each cavity about 2/3 full.',
      'Bake for 10-12 minutes until golden and a toothpick inserted comes out clean.',
      'Let cool in pan for 5 minutes, then transfer to a wire rack.',
      'Store in an airtight container at room temperature for up to 3 days.',
      'Naturally sweetened with dates, made with whole grain flour, baked. No refined sugar, high fiber.'
    ],
    notes: 'Naturally sweetened with dates, made with whole grain flour, baked. No refined sugar, high fiber.'
  },

  'pumpkin-spice-donut-holes-baked': {
    title: 'Pumpkin Spice Donut Holes (Baked)',
    cuisine: 'American',
    servings: 12,
    caloriesPerServing: 112,
    ingredients: [
      { name: 'pumpkin puree', amount: 0.5, unit: 'cup' },
      { name: 'whole wheat flour', amount: 1, unit: 'cup' },
      { name: 'eggs', amount: 1, unit: 'piece', notes: 'large' },
      { name: 'milk', amount: 0.5, unit: 'cup', notes: 'almond milk, coconut milk, or regular milk' },
      { name: 'coconut oil', amount: 2, unit: 'tbsp', notes: 'melted' },
      { name: 'baking powder', amount: 1, unit: 'tsp' },
      { name: 'cinnamon', amount: 0.5, unit: 'tsp', notes: 'ground' },
      { name: 'ginger', amount: 0.25, unit: 'tsp', notes: 'ground' },
      { name: 'nutmeg', amount: 0.125, unit: 'tsp', notes: 'ground' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C). Grease a mini muffin tin or donut hole pan.',
      'In a large bowl, whisk together pumpkin puree, egg, milk, and melted coconut oil.',
      'In a separate bowl, mix whole wheat flour, baking powder, cinnamon, ginger, nutmeg, and salt.',
      'Add dry ingredients to wet ingredients and mix until just combined.',
      'Spoon batter into prepared pan, filling each cavity about 2/3 full.',
      'Bake for 10-12 minutes until golden and a toothpick inserted comes out clean.',
      'Let cool in pan for 5 minutes, then transfer to a wire rack.',
      'Store in an airtight container at room temperature for up to 3 days.',
      'Made with pumpkin puree and whole grain flour, baked. High in Vitamin A, lower sugar.'
    ],
    notes: 'Made with pumpkin puree and whole grain flour, baked. High in Vitamin A, lower sugar.'
  },

  // ============================================
  // PIE RECIPES
  // ============================================
  'crustless-fruit-pie': {
    title: 'Crustless Fruit Pie',
    cuisine: 'American',
    servings: 8,
    caloriesPerServing: 180,
    ingredients: [
      { name: 'mixed fruit', amount: 4, unit: 'cup', notes: 'fresh or frozen (berries, peaches, apples, etc.)' },
      { name: 'maple syrup', amount: 2, unit: 'tbsp', notes: 'or honey, optional' },
      { name: 'cinnamon', amount: 1, unit: 'tsp', notes: 'ground' },
      { name: 'lemon juice', amount: 1, unit: 'tbsp', notes: 'fresh' },
      { name: 'cornstarch', amount: 2, unit: 'tbsp', notes: 'or arrowroot powder, for thickening' }
    ],
    steps: [
      'Preheat oven to 375°F (190°C). Grease a 9-inch pie dish or baking dish.',
      'If using fresh fruit, wash and slice as needed. If using frozen fruit, let it thaw slightly.',
      'In a large bowl, toss fruit with maple syrup (if using), cinnamon, and lemon juice.',
      'Sprinkle with cornstarch and toss to coat evenly.',
      'Pour fruit mixture into the prepared dish.',
      'Bake for 30-40 minutes until fruit is tender and juices are bubbling.',
      'Let cool for at least 30 minutes before serving.',
      'Serve warm or at room temperature. Store in the refrigerator for up to 3 days.',
      'Baked fruit filling without the refined flour crust. Lower carbs, higher fiber, naturally sweet.'
    ],
    notes: 'Baked fruit filling without the refined flour crust. Lower carbs, higher fiber, naturally sweet.'
  },

  'whole-grain-fruit-pie': {
    title: 'Whole Grain Fruit Pie',
    cuisine: 'American',
    servings: 8,
    caloriesPerServing: 240,
    ingredients: [
      { name: 'whole wheat flour', amount: 2, unit: 'cup' },
      { name: 'coconut oil', amount: 0.5, unit: 'cup', notes: 'solid, cold' },
      { name: 'ice water', amount: 4, unit: 'tbsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' },
      { name: 'mixed fruit', amount: 4, unit: 'cup', notes: 'fresh or frozen (berries, peaches, apples, etc.)' },
      { name: 'maple syrup', amount: 0.25, unit: 'cup', notes: 'or honey' },
      { name: 'cinnamon', amount: 1, unit: 'tsp', notes: 'ground' },
      { name: 'cornstarch', amount: 2, unit: 'tbsp', notes: 'for thickening' }
    ],
    steps: [
      'Preheat oven to 375°F (190°C).',
      'Make crust: In a large bowl, mix whole wheat flour and salt.',
      'Cut cold coconut oil into the flour mixture until it resembles coarse crumbs.',
      'Add ice water, 1 tbsp at a time, mixing until dough comes together.',
      'Divide dough in half. Roll out one half and place in a 9-inch pie dish.',
      'Make filling: In a large bowl, toss fruit with maple syrup, cinnamon, and cornstarch.',
      'Pour filling into the prepared crust.',
      'Roll out the second half of dough and place on top. Seal edges and cut slits in the top.',
      'Bake for 45-50 minutes until crust is golden and filling is bubbling.',
      'Let cool for at least 1 hour before serving.',
      'Made with whole wheat or oat flour crust, naturally sweetened fruit filling. Higher fiber, lower sugar.'
    ],
    notes: 'Made with whole wheat or oat flour crust, naturally sweetened fruit filling. Higher fiber, lower sugar.'
  },

  'almond-flour-pie-crust-with-fruit': {
    title: 'Almond Flour Pie Crust with Fruit',
    cuisine: 'American',
    servings: 8,
    caloriesPerServing: 220,
    ingredients: [
      { name: 'almond flour', amount: 1.5, unit: 'cup' },
      { name: 'coconut oil', amount: 0.25, unit: 'cup', notes: 'melted' },
      { name: 'maple syrup', amount: 1, unit: 'tbsp' },
      { name: 'salt', amount: 0.25, unit: 'tsp' },
      { name: 'mixed fruit', amount: 4, unit: 'cup', notes: 'fresh (berries, peaches, apples, etc.)' },
      { name: 'maple syrup', amount: 0.25, unit: 'cup', notes: 'for filling' },
      { name: 'cinnamon', amount: 1, unit: 'tsp', notes: 'ground' },
      { name: 'cornstarch', amount: 2, unit: 'tbsp', notes: 'for thickening' }
    ],
    steps: [
      'Preheat oven to 350°F (175°C).',
      'Make crust: In a large bowl, mix almond flour, melted coconut oil, 1 tbsp maple syrup, and salt until a dough forms.',
      'Press dough into a 9-inch pie dish, covering the bottom and sides.',
      'Bake crust for 10 minutes until slightly golden. Let cool.',
      'Make filling: In a large bowl, toss fruit with 0.25 cup maple syrup, cinnamon, and cornstarch.',
      'Pour filling into the baked crust.',
      'Bake for 30-40 minutes until filling is bubbling.',
      'Let cool for at least 1 hour before serving.',
      'Crust made from almond flour, filled with fresh fruit. Lower glycemic, healthy fats, no refined flour.'
    ],
    notes: 'Crust made from almond flour, filled with fresh fruit. Lower glycemic, healthy fats, no refined flour.'
  },

  'pumpkin-pie-with-whole-grain-crust': {
    title: 'Pumpkin Pie with Whole Grain Crust',
    cuisine: 'American',
    servings: 8,
    caloriesPerServing: 230,
    ingredients: [
      { name: 'whole wheat flour', amount: 1.5, unit: 'cup' },
      { name: 'coconut oil', amount: 0.5, unit: 'cup', notes: 'solid, cold' },
      { name: 'ice water', amount: 4, unit: 'tbsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' },
      { name: 'pumpkin puree', amount: 1.5, unit: 'cup' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'maple syrup', amount: 0.33, unit: 'cup' },
      { name: 'coconut milk', amount: 0.5, unit: 'cup', notes: 'full-fat' },
      { name: 'cinnamon', amount: 1, unit: 'tsp', notes: 'ground' },
      { name: 'ginger', amount: 0.5, unit: 'tsp', notes: 'ground' },
      { name: 'nutmeg', amount: 0.25, unit: 'tsp', notes: 'ground' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 375°F (190°C).',
      'Make crust: In a large bowl, mix whole wheat flour and salt. Cut cold coconut oil into flour until it resembles coarse crumbs. Add ice water, 1 tbsp at a time, until dough comes together. Roll out and place in a 9-inch pie dish.',
      'Make filling: In a large bowl, whisk together pumpkin puree, eggs, maple syrup, coconut milk, cinnamon, ginger, nutmeg, and salt.',
      'Pour filling into the prepared crust.',
      'Bake for 45-50 minutes until filling is set and crust is golden.',
      'Let cool completely before serving. Store in the refrigerator for up to 3 days.',
      'Made with whole grain crust, naturally sweetened pumpkin filling. High in Vitamin A, lower sugar.'
    ],
    notes: 'Made with whole grain crust, naturally sweetened pumpkin filling. High in Vitamin A, lower sugar.'
  },

  'apple-pie-with-oat-flour-crust': {
    title: 'Apple Pie with Oat Flour Crust',
    cuisine: 'American',
    servings: 8,
    caloriesPerServing: 245,
    ingredients: [
      { name: 'oat flour', amount: 2, unit: 'cup', notes: 'or grind 2.5 cups rolled oats' },
      { name: 'coconut oil', amount: 0.5, unit: 'cup', notes: 'solid, cold' },
      { name: 'ice water', amount: 4, unit: 'tbsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' },
      { name: 'apples', amount: 6, unit: 'piece', notes: 'medium, peeled and sliced' },
      { name: 'maple syrup', amount: 0.25, unit: 'cup' },
      { name: 'cinnamon', amount: 1, unit: 'tsp', notes: 'ground' },
      { name: 'lemon juice', amount: 1, unit: 'tbsp', notes: 'fresh' },
      { name: 'cornstarch', amount: 2, unit: 'tbsp', notes: 'for thickening' }
    ],
    steps: [
      'Preheat oven to 375°F (190°C).',
      'If making oat flour: Grind 2.5 cups rolled oats in a food processor until fine.',
      'Make crust: In a large bowl, mix oat flour and salt. Cut cold coconut oil into flour until it resembles coarse crumbs. Add ice water, 1 tbsp at a time, until dough comes together. Divide in half. Roll out one half and place in a 9-inch pie dish.',
      'Make filling: In a large bowl, toss sliced apples with maple syrup, cinnamon, lemon juice, and cornstarch.',
      'Pour filling into the prepared crust.',
      'Roll out the second half of dough and place on top. Seal edges and cut slits in the top.',
      'Bake for 45-50 minutes until crust is golden and filling is bubbling.',
      'Let cool for at least 1 hour before serving.',
      'Crust made from oat flour, filled with naturally sweetened apples. Higher fiber, lower sugar.'
    ],
    notes: 'Crust made from oat flour, filled with naturally sweetened apples. Higher fiber, lower sugar.'
  },

  'berry-crumble-no-bottom-crust': {
    title: 'Berry Crumble (No Bottom Crust)',
    cuisine: 'American',
    servings: 8,
    caloriesPerServing: 200,
    ingredients: [
      { name: 'mixed berries', amount: 4, unit: 'cup', notes: 'fresh or frozen (strawberries, blueberries, raspberries, blackberries)' },
      { name: 'maple syrup', amount: 2, unit: 'tbsp', notes: 'or honey' },
      { name: 'lemon juice', amount: 1, unit: 'tbsp', notes: 'fresh' },
      { name: 'cornstarch', amount: 1, unit: 'tbsp', notes: 'for thickening' },
      { name: 'rolled oats', amount: 1, unit: 'cup' },
      { name: 'almond flour', amount: 0.5, unit: 'cup' },
      { name: 'coconut oil', amount: 0.25, unit: 'cup', notes: 'melted' },
      { name: 'maple syrup', amount: 2, unit: 'tbsp', notes: 'for topping' },
      { name: 'cinnamon', amount: 0.5, unit: 'tsp', notes: 'ground' }
    ],
    steps: [
      'Preheat oven to 375°F (190°C). Grease a 9-inch baking dish.',
      'In a large bowl, toss berries with 2 tbsp maple syrup, lemon juice, and cornstarch.',
      'Pour berry mixture into the prepared dish.',
      'Make crumble topping: In a separate bowl, mix rolled oats, almond flour, melted coconut oil, 2 tbsp maple syrup, and cinnamon until crumbly.',
      'Sprinkle crumble topping evenly over the berries.',
      'Bake for 30-40 minutes until berries are bubbling and topping is golden.',
      'Let cool for at least 30 minutes before serving.',
      'Baked berries with a whole grain crumble topping, no bottom crust. Lower carbs, higher fiber.'
    ],
    notes: 'Baked berries with a whole grain crumble topping, no bottom crust. Lower carbs, higher fiber.'
  },

  'sweet-potato-pie-with-whole-grain-crust': {
    title: 'Sweet Potato Pie with Whole Grain Crust',
    cuisine: 'American',
    servings: 8,
    caloriesPerServing: 235,
    ingredients: [
      { name: 'whole wheat flour', amount: 1.5, unit: 'cup' },
      { name: 'coconut oil', amount: 0.5, unit: 'cup', notes: 'solid, cold' },
      { name: 'ice water', amount: 4, unit: 'tbsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' },
      { name: 'sweet potato', amount: 2, unit: 'cup', notes: 'cooked and mashed' },
      { name: 'eggs', amount: 2, unit: 'piece', notes: 'large' },
      { name: 'maple syrup', amount: 0.33, unit: 'cup' },
      { name: 'coconut milk', amount: 0.5, unit: 'cup', notes: 'full-fat' },
      { name: 'cinnamon', amount: 1, unit: 'tsp', notes: 'ground' },
      { name: 'ginger', amount: 0.5, unit: 'tsp', notes: 'ground' },
      { name: 'nutmeg', amount: 0.25, unit: 'tsp', notes: 'ground' },
      { name: 'salt', amount: 0.5, unit: 'tsp' }
    ],
    steps: [
      'Preheat oven to 375°F (190°C).',
      'Make crust: In a large bowl, mix whole wheat flour and salt. Cut cold coconut oil into flour until it resembles coarse crumbs. Add ice water, 1 tbsp at a time, until dough comes together. Roll out and place in a 9-inch pie dish.',
      'Cook and mash sweet potato until smooth. Measure 2 cups.',
      'Make filling: In a large bowl, whisk together mashed sweet potato, eggs, maple syrup, coconut milk, cinnamon, ginger, nutmeg, and salt.',
      'Pour filling into the prepared crust.',
      'Bake for 45-50 minutes until filling is set and crust is golden.',
      'Let cool completely before serving. Store in the refrigerator for up to 3 days.',
      'Made with whole grain crust, naturally sweetened sweet potato filling. High in beta-carotene, lower sugar.'
    ],
    notes: 'Made with whole grain crust, naturally sweetened sweet potato filling. High in beta-carotene, lower sugar.'
  },

  'date-sweetened-fruit-pie': {
    title: 'Date-Sweetened Fruit Pie',
    cuisine: 'American',
    servings: 8,
    caloriesPerServing: 210,
    ingredients: [
      { name: 'whole wheat flour', amount: 1.5, unit: 'cup' },
      { name: 'coconut oil', amount: 0.5, unit: 'cup', notes: 'solid, cold' },
      { name: 'ice water', amount: 4, unit: 'tbsp' },
      { name: 'salt', amount: 0.5, unit: 'tsp' },
      { name: 'mixed fruit', amount: 4, unit: 'cup', notes: 'fresh (berries, peaches, apples, etc.)' },
      { name: 'dates', amount: 0.5, unit: 'cup', notes: 'pitted, medjool dates preferred' },
      { name: 'cinnamon', amount: 1, unit: 'tsp', notes: 'ground' },
      { name: 'lemon juice', amount: 1, unit: 'tbsp', notes: 'fresh' },
      { name: 'cornstarch', amount: 2, unit: 'tbsp', notes: 'for thickening' }
    ],
    steps: [
      'Preheat oven to 375°F (190°C).',
      'Make crust: In a large bowl, mix whole wheat flour and salt. Cut cold coconut oil into flour until it resembles coarse crumbs. Add ice water, 1 tbsp at a time, until dough comes together. Divide in half. Roll out one half and place in a 9-inch pie dish.',
      'Soak dates in hot water for 10 minutes to soften. Drain and reserve 2 tbsp of the soaking water.',
      'In a food processor, blend soaked dates with reserved water until a smooth paste forms.',
      'Make filling: In a large bowl, toss fruit with date paste, cinnamon, lemon juice, and cornstarch.',
      'Pour filling into the prepared crust.',
      'Roll out the second half of dough and place on top. Seal edges and cut slits in the top.',
      'Bake for 45-50 minutes until crust is golden and filling is bubbling.',
      'Let cool for at least 1 hour before serving.',
      'Naturally sweetened with dates, made with whole grain crust. No refined sugar, high fiber.'
    ],
    notes: 'Naturally sweetened with dates, made with whole grain crust. No refined sugar, high fiber.'
  }
};

// Recipe matching function with fuzzy matching logic
export function getRecipeForAlternative(alternativeName: string, cuisine?: string): AlternativeRecipe | undefined {
  // Normalize the alternative name for matching
  const normalizedName = alternativeName.toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '-')
    .trim();
  
  // Try exact match first
  if (RECIPE_CATALOG[normalizedName]) {
    return RECIPE_CATALOG[normalizedName];
  }
  
  // Try partial matches
  for (const [key, recipe] of Object.entries(RECIPE_CATALOG)) {
    const keyWords = key.split('-');
    const altWords = normalizedName.split('-');
    
    // Check if key words match alternative words
    const matchingWords = keyWords.filter(kw => altWords.some(aw => aw.includes(kw) || kw.includes(aw)));
    
    if (matchingWords.length >= 2) {
      // Check cuisine match if specified
      if (cuisine && recipe.cuisine.toLowerCase() !== cuisine.toLowerCase()) {
        continue;
      }
      return recipe;
    }
  }
  
  return undefined;
}

/**
 * Creates a fallback recipe when no catalog match exists.
 * Ensures every food alternative can show a "View Recipe" button.
 */
export function createFallbackRecipe(
  alternativeName: string,
  description?: string,
  cuisine?: string
): AlternativeRecipe {
  return {
    title: alternativeName,
    cuisine: cuisine || 'General',
    servings: 2,
    caloriesPerServing: 350,
    ingredients: [
      { name: 'Whole grains or vegetable base', amount: 1, unit: 'portion', notes: 'e.g., whole wheat, cauliflower, zucchini' },
      { name: 'Fresh vegetables', amount: 1, unit: 'cup', notes: 'chopped' },
      { name: 'Olive oil or healthy fat', amount: 1, unit: 'tbsp', notes: 'minimal amount' },
      { name: 'Herbs and spices', amount: 0.5, unit: 'tsp', notes: 'to taste' },
      { name: 'Salt and pepper', amount: 0.25, unit: 'tsp', notes: 'to taste' }
    ],
    steps: [
      'Review the description above for key ingredients and approach.',
      'Use whole grains, vegetables, and minimal oil where possible.',
      'Prepare using baking, grilling, or steaming instead of frying when applicable.',
      'Add vegetables and lean protein for a balanced meal.',
      'Season to taste and serve.'
    ],
    notes: description || 'A healthier alternative with skin-friendly ingredients. Customize based on the description above and your dietary preferences.'
  };
}
